import { InjectionToken } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BrandingService } from '../services/branding.service';

export const ERROR_URL = new InjectionToken<string>('Error Url', { providedIn: 'root', factory: () => '/error' });

export const EXTENSION_URL = 'https://chrome.google.com/webstore/detail/time-doctor-2/hhchghdnpepogpigkpalpljaoocjhadj';
export const CHROMEOS_EXTENSION_URL = 'https://chrome.google.com/webstore/detail/time-doctor-2/fpfkmegplkpabeceoljjeajajeoiimim';
export const FIREFOX_ADDON_URL = 'https://addons.mozilla.org/en-US/firefox/addon/time-doctor-2/';

export const IOS_APP_URL = 'https://apps.apple.com/us/app/time-doctor-2/id6447000777';
export const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.timedoctor2.mobile';

function deserializeVariable(variable: string) {
  if (!variable) return variable;
  if (variable === 'undefined') return undefined;

  try {
    return JSON.parse(variable);
  } catch (e) { }

  return variable;
}

export function getFromQueryOrStorage(param: string) {
  const urlParams = typeof URLSearchParams === 'function' ? new URLSearchParams(window.location.search?.toLowerCase()) : null;
  const fromQuery = urlParams?.get(param?.toLowerCase());
  if (fromQuery != null) return deserializeVariable(fromQuery);

  const fromSession = sessionStorage.getItem(`overrides.${param}`);
  if (fromSession != null) return deserializeVariable(fromSession);

  const fromLocal = localStorage.getItem(`overrides.${param}`);
  if (fromLocal != null) return deserializeVariable(fromLocal);

  return null;
}

export function getSplitTestEnabled(ratio: number, storagePrefix: string): boolean {
  if (!Number.isFinite(ratio) || ratio <= 0) return false;
  if (ratio >= 100) return true;
  const stored = localStorage.getItem(`td.splitTest.${storagePrefix}`);
  const storedNumber = stored ? Number(stored) : null;
  if (storedNumber !== null && !isNaN(storedNumber)) {
    return storedNumber < ratio;
  }
  const normalized = Math.floor(Math.random() * 100);
  localStorage.setItem(`td.splitTest.${storagePrefix}`, String(normalized));

  return normalized < ratio;
}

export function getSocketUrl(branding: BrandingService): string {
  const fromQuery = getFromQueryOrStorage('api') || branding.getApiUrl();
  if (typeof fromQuery !== 'string') { return environment.socketHost; }
  return fromQuery.replace(/^https?:\/\//, '');
}

export function getApiUrl(branding: BrandingService): string {
  const fromQuery = getFromQueryOrStorage('api') || branding.getApiUrl();
  if (typeof fromQuery !== 'string') { return environment.apiUrl; }
  const protocol = fromQuery.startsWith('http') ? '' : 'https://';
  const url = new URL('/api/{version}', `${protocol}${fromQuery}`);
  return decodeURI(url.href);
}

export function getDisableBilling(): boolean {
  const disable = getFromQueryOrStorage('disableBilling');
  if (typeof disable === 'boolean') { return disable; }
  return environment.env.TD_APP_DISABLE_BILLING === 'true';
}

export function getDisableScreencasts(): boolean {
  const disable = getFromQueryOrStorage('disableScreencasts');
  if (typeof disable === 'boolean') { return disable; }
  return environment.env.TD_APP_DISABLE_SCREENCASTS === 'true';
}

export function getDisableOnboarding(): boolean {
  const disable = getFromQueryOrStorage('disableOnboarding');
  if (typeof disable === 'boolean') { return disable; }
  return environment.env.TD_APP_DISABLE_ONBOARDING === 'true';
}

export function getDisableInvite(): boolean {
  const disable = getFromQueryOrStorage('disableInvite');
  if (typeof disable === 'boolean') { return disable; }
  return environment.env.TD_APP_DISABLE_INVITE === 'true';
}

export function getDisableDownload(): boolean {
  const disable = getFromQueryOrStorage('disableDownload');
  if (typeof disable === 'boolean') { return disable; }
  return environment.env.TD_APP_DISABLE_DOWNLOAD === 'true';
}

export function getAllowSocket(): boolean {
  const allow = getFromQueryOrStorage('allowSocket');
  if (typeof allow === 'boolean') { return allow; }
  return environment.allowSocket;
}

export function getUseSocketApi(): boolean {
  if (!getAllowSocket()) return false;
  const dev = getFromQueryOrStorage('dev');
  const socket = getFromQueryOrStorage('socket');
  if (typeof socket === 'boolean') { return socket; }
  if (typeof dev === 'boolean') { return !dev; }
  return environment.useSocketApi;
}

export function getHttpTimeout(): number {
  const httpTimeout = getFromQueryOrStorage('httpTimeout');
  if (typeof httpTimeout === 'number') { return httpTimeout * 1000; }
  return environment.httpTimeout;
}

export const defualtUserApiLimit = 400;
export const defualtWorkscheduleApiLimit = 500;

const isAlphaQuery = !!getFromQueryOrStorage('alpha');

export const isBeta = !!getFromQueryOrStorage('beta');

export const isExecutive = !!getFromQueryOrStorage('executive');

const alphaCustomers = [
  'VcKxVBrmYBMArp5C', // Time Doctor
  'YI7NjZfhhwAE4wUn', // Swipewire
];

const betaCustomers = [
  'VcKxVBrmYBMArp5C', // Time Doctor
  'W0QAPz_26wB9ZPhg', // AMN Distribution
  'W61eIzIpSAE6uTdL', // Lava Seats
  // Removed from Beta Customers for release of Breaks.
  // More info https://globalworkforce.jira.com/browse/STAFF-125146?focusedCommentId=353751
  // 'W-yCxK1X6AB2qTMY', // Ascend BPO
  'XKT9nqR01AAg22SD', // TempDev
  'XYTcA_XjXgAg-5w8', // Task Drive (TD-LTV+)
  'XadAqZSuEAAZHarB', // AGM Agency (Manuel Suarez)
  'XjebAjMENwAEFwCx', // Freedom.tm (Freedom! Family Limited)
  'XnDiG-h3PgAEOqMA', // Tada
  'XtUc0lplEQAEurs4', // Gogotech ll LLC
  'XLWcZ99nJgAfoEWi', // Beepo Australia
  'YEjDYSgiyQAElHP4', // QUIVER DESENVOLVIMENTO E TECNOLOGIA
  'X46_S6jwzwAEF9-f', // Carecone pty Ltd
  'YLjCupnoyQAE0q46', // Peak Support
  'XoqCfpppyAAExYnN', // Filta
  'YGz0i0lxuwAEfupt', // FiltaGlobal
  'Xnga_m5ElAAEOnmK', // Personiv
  'XsLcnhaFzQAE8Tsm', // Yeetiq
  'X6hUPaXf8AAEogSU', // RepStack
  'YUS-Vwb4KicD1RUB', // eXp Realty
  'XLS4h1qc_wAdgKL-', // Dentistry Support
  'Yk86evgmqCsEKmqx', // Greek House
  'YFlizoFDbAAEv6g8', // Circles.Life
  'Yl6n1RSjVRibUAse', //Sipuni RUS
  'YqBSLKqW9XtoBzYe', //GymBeam
  'YvdDiMJklFi5Y76r', //AppCake
  'Y1m9bTk9L3JBCmaE', //Simpress
  'Yk-uDH8ErvTkUJem',// Stafflink
];

const timeDoctorSalesCompanyId = 'X4DHsJcjcgAE2QHW';

interface McpRolloutBatch {
  flag: string;
  companies: ReadonlySet<string>;
}

const mcpRolloutBatches: readonly McpRolloutBatch[] = [
  {
    flag: 'mcp-rollout-batch-1',
    companies: new Set([
      'X4DHsJcjcgAE2QHW', // TD Revenue account
      'VcKxVBrmYBMArp5C', // Time Doctor (staging)
      'Z8U2LwDrqyacSvQJ', // test company (staging)
    ]),
  },
  {
    flag: 'mcp-rollout-batch-2',
    companies: new Set([
      'Z1IgF77HL1XE6WLd',
      'Z1JbxtS2Gspsc1uZ',
      'Z1KHDdtx24a5qtW9',
      'Z1NYJ7FSRIvgiMhC',
      'Z1dJsK6l5A3JkezR',
      'Z1fEYNF0Kztfg5Q_',
      'Z1hoacAg0iwemeOb',
      'Z1m3IkcTY5kyv2Zh',
      'Z10VFukQxjG1503l',
      'Z1-Bj1H8CvucEl0h',
      'Z2AvHekQxjG16oj3',
      'Z2B-tfgI3GUNMJS1',
      'Z2I9UvgI3GUNNE4o',
      'Z2KxJukQxjG18BHu',
      'Z2MXpgGyGnjQUmir',
      'Z28i1GpODHrUl6V_',
      'Z3P7Pw__P31aIGbE',
      'Z3VDzA__P31aIahb',
      'Z3XDKDnIToHEP-Sn',
      'Z3YR_Q__P31aImlF',
      'Z3b0onf3YuoEvpao',
      'Z3fWxA__P31aJfre',
      'Z3hFejnIToHERP6h',
      'Z3hXDg__P31aJ1lI',
      'Z3vAVywSn4xCAGgS',
      'Z3ytUrZ7Y3Sm4Wjp',
      'Z33HI7Z7Y3Sm5DXX',
      'Z35jJrZ7Y3Sm5VLW',
      'Z4AHXFzrSFYFOsoV',
      'Z4Rot-AomkgDdCZl',
      'Z4Tq_uAomkgDdCbv',
      'Z4jr_1AIUDU68h_H',
      'Z4lUkwzJA-GkM_l6',
      'Z4lmnBA-aLgbeVAB',
      'Z4qaVcYHyhB4XQuC',
      'Z43IquKoPMLdbrMr',
      'Z46FnXNTXS1f_8fI',
      'Z46VtHNTXS1f_8fa',
      'Z46auUvtAiussa_R',
      'Z48eXjRnpUFLwejj',
      'Z4-fBlXm9veiWuGa',
      'Z5EeX1lJ-4-wha-3',
      'Z5FOilcdz20gqd1x',
      'Z5J7cscqz-Vg_tLQ',
      'Z5Kmfccqz-Vg_tLc',
      'Z5QQBUBExMGdDnBD',
      'Z5ZYHF7sRqoVRffq',
      'Z5cTvyUAZWr7MDBm',
      'Z5ecTJen2J_mAF_R',
      'Z5fFQS7KXAZvD4K5',
      'Z5j5P23TJXp-lzdG',
      'Z5mQZdm8DDeWbXqw',
      'Z5pSbTIEH04Pw9jV',
      'Z5q1orbJoe4FDJ1v',
      'Z5-0hESVmPiG_pQt',
      'Z6DABd_mSxveja9c',
      'Z6FG1jHSK_Ig2jS4',
      'Z6KgTGu1hDaV_gip',
      'Z6Pr2zIthP3D7MBP',
      'Z6TuRAINyBf2FZJY',
      'Z6tCubZCVVxwWfIj',
      'Z6t0N7DBWmgcXJz-',
      'Z6wfhhrtEaEfK09a',
      'Z6xSYOISX4BwgFVr',
      'Z60SZ7DBWmgc-4lb',
      'Z62TdE49DHK9qk21',
      'Z62mNrDBWmgcLBHG',
      'Z64AtxYUCHQxfJ-K',
      'Z64aeDmbTSeDFCjR',
      'Z69ytR2fYHN-Oq0l',
      'Z7HBEu0BV_0AYdnS',
      'Z7Mn7f0Z_7SYkWF7',
      'Z7M8dOOiZh7ZqQfu',
      'Z7N2pkn41d8vRDAz',
      'Z7OhlP2df1FYfWkL',
      'Z7TJiuTBBWhClUVr',
      'Z7ZNMXN9Dl4vG6XZ',
      'Z7eAlKmPPv2RUOut',
      'Z7es2pxoZc1dxs7I',
      'Z7wDidFeTMdrU3fW',
      'Z7w1VNFeTMdrYOrh',
      'Z7w6k27WtEWVMjNI',
      'Z7yByuLA3IcvDxWV',
      'Z7y3QOAzG3eGSVyG',
      'Z73NPTbzpBxTg1GS',
      'Z73SebeSAUSyzrXE',
      'Z78NGsQYNwJbREtP',
      'Z78d9FIrswWiP31-',
      'Z8Inmi5whNE5jqUw',
      'Z8KRzRO-U-Ti-GDs',
      'Z8O15e_evRa1ZuDa',
      'Z8SJ3RhJUS-h1EZ4',
      'Z8Yz7xhJUS-hkMLB',
      'Z8Y6TqGgJSziwPvH',
      'Z8ZqaLuEvrLiqiED',
      'Z8ocdYRWBenWiIvN',
      'Z8op_3EnHXvaRPAz',
      'Z8wbzkvrKtQdqjE_',
      'Z87tJwgKOEJL8o1q',
      'Z8_h4IR70JLr6svH',
      'Z9AfJXHHrUvfHJ3r',
      'Z9EhVHR38Oneh32d',
      'Z9GCvYJotm65nGxS',
      'Z9RLnG0s6sB0Yf8C',
      'Z9ojWP9GYvuRP-L5',
      'Z9rXJrE1sQf5GSbo',
      'Z9w7BWAxkQf1It-g',
      'Z93SvjO8LC67qJ47',
      'Z-DZ9gQfc_VaEgMO',
      'Z-LjtCtMLKpovB5n',
      'Z-P5V_rKaNWT925v',
      'Z-QOrAAVWgFLjNfk',
      'Z-gE7373DufzpBy0',
      'Z-sRDqSHno8HtuNZ',
      'Z-tNwhCBf7kjoUII',
      'Z-vwD8jjfuMIFyed',
      'Z-wmeFgTxS6i1Rvn',
      'Z-xr5KCYj0oo-72c',
      'Z-1W8uNIQvnVfRJ1',
      'Z-1Y4nDN0qxJtgl-',
      'Z-3O_QTxs6OxgGeW',
      'Z-49iAOMH9VwVK1i',
      'Z-5alPbVAYHKE1OU',
      'Z-79xPbVAYHKayyR',
      'Z_ACRZZG9zBSfS_8',
      'Z_JIGqiID0jZpW0I',
      'Z_Olo-EALFB3Ix7J',
      'Z_Q-vuA_0_K7AM-x',
      'Z_SoweA_0_K7NRL4',
      'Z_VW3imU2SelOip6',
      'Z_WONOA_0_K7jt4s',
      'Z_Z0af42-G671m09',
      'Z_gV2mwcbRyzp6cW',
      'Z_h6ygoLLEQCvUxy',
      'Z_0_e8a5MowYjcHo',
      'Z_2IamwcbRyzJWnn',
      'aAAVGDmC4BTVwdUR',
      'aAAyzc_zLJ0pOMXU',
      'aAWT_MaB32s5aMxv',
      'aAX-msaB32s5im9i',
      'aAjMs_jsLmmlQ4r8',
      'aAmL_PmzUBn6J8LY',
      'aAoWi1eI-WM83QY2',
      'aAvBjauZHiCnk8Lk',
      'aA7eYZyG3jqpjyqw',
      'aA9KFSMzGI2JUOZh',
      'aA-FcX9eOVQrh8td',
      'aA-dBlYKR6TKtD1n',
      'aA_t5ZKbqEahg65A',
      'aBGHwZAqXyyaOrs_',
      'aBIE9KQ4hXrSeI8w',
      'aBJqt2SqSpAH00IZ',
      'aBKUBnin5WGu_VoP',
      'aBOa1AZd-bRlMiQ3',
      'aBSSxoEhihsPt2k_',
      'aBTaXFu6Be6fSmt_',
      'aBUU523uCeAKpPqE',
      'aBd5ZTkuw3fmaATq',
      'aBhX800On6kFKrKS',
      'aBkf5Nqrz6BqRC1p',
      'aBopePSB_jbYVLJb',
      'aBpUnpwSef04-tHc',
      'aByrpcFS_jQgAl8B',
      'aBzc3LPL1wRrsOL8',
      'aB11fVFGwVHylVQW',
      'aB4_TCS3E5PZMzlK',
      'aB6KDSu-7P0JwOSS',
      'aCE9NaRsEp79O0wr',
      'aCIDcFp9poI0sLwO',
      'aCIOr5THnpxtng-Q',
      'aCKZz_qVjMyYUBgR',
      'aCT-JU5z4xAblTXZ',
      'aCdj6Xbu7sQyPHRa',
      'aCexU8P1ympz6A1V',
      'aCmJ0eet6oWUUL_x',
      'aCsdliOZdFvcc85p',
      'aCy0G8P9AdVMuEIk',
      'aC9oiXcCVpW-Evuk',
      'aDB2bDy_FQSWdOz1',
      'aDIcNPdOhqNIjfGe',
      'aDNAXM_DT7pZUKVP',
      'aDSTluUACFfz81iN',
      'aDWSc8673V8EK2Ow',
      'aDbG-P7wMtJNLtTY',
      'aDbhk6Asi7LRc8sL',
      'aDcGT_7wMtJNSVjU',
      'aDc4zVdeyzOS5xKi',
      'aDguK0PUxUBnoRke',
      'aDhOSpp1o23At2IV',
      'aDiP9sOiOkjtqM3b',
      'aDjUcsOiOkjtyPix',
      'aDnorB1xf24OHEqD',
      'aDtgMr5bpaViKcMs',
      'aDvQK75bpaViOl_F',
      'aDyqD75bpaViYJ1h',
      'aD0gSqywSgmLPEdq',
      'aD8Q6fXKxCKWwxlW',
      'aD9xjsr5bp5ArcX-',
      'aEFsd4H4Oq66CiZV',
      'aEKBy-0P0-1KibsH',
      'aEQfLYXkDZn22VrI',
      'aEYb9hMN6kubcnEF',
      'aEfegUTJFHgu0CPv',
      'aEmkCvnlYw2mtC45',
      'aE9x1SIM6dRLT7Xh',
      'aE--3cbFxWB1LOm8',
      'aE_Rtyghq9sFGmms',
      'aFBwigu2NkEXUFCl',
      'aFGWQq_rwFrRS7RX',
      'aFHivoRrT6T22wEN',
      'aFI9uHCptlrKlL9B',
      'aFKlK_LfaCR31TJf',
      'aFRer5hDG6yUNMOY',
      'aFSTAH-sZFiwCMfn',
      'aFhwZflSvMZrQEe_',
      'aFk-mzW529vBclkV',
      'aFpWKj2nmTHwpNLf',
      'aFyTzaffK0aSwRvF',
      'aFyWyPT4clnmaHDI',
      'aF2r8YCMY5rw7IUp',
      'aF7Q9RqTSu6CFO5X',
      'aF_UEs67lNWE4l4c',
      'aGHuq-cXEKKnsHCe',
      'aGM7nFXZIjdNgaBT',
      'aGNVUVXZIjdNisBb',
      'aGN2e9VYpRl8XvyN',
      'aGYi9Xjv7mXqRiN0',
      'aGeu_ACwGZh5Vi-r',
      'aGg1twcvFpvkZLhd',
      'aGj8mQCwGZh50jBo',
      'aGkPMgCwGZh51iNk',
      'aGmBXQCwGZh577sI',
      'aGtUFQCwGZh5TaOZ',
      'aGwZ3kBuPTylWdg_',
      'aGwp4fV3h3vKCMCb',
      'aGxFHdCOTHD_lSe-',
      'aGxlNr_e7OORLYHD',
      'aGxp01khqMfKeUoY',
      'aGyxm7_e7OORSCCc',
      'aG3LhZnRCSGagoH4',
      'aG6ZIPI30rWgk4RZ',
      'aG7BRbFueGv4NMl5',
      'aG-3CSKymBpN8zNo',
      'aG_Fh5aT7epIFfOO',
      'aHALOw2EmFdVHhqc',
      'aHBpug2EmFdVUr9E',
      'aHJxumGOJLVsR45K',
      'aHKqGhcTrMikyQnD',
      'aHNgZmGOJLVscKDl',
      'aHXTV2GOJLVsTGy2',
      'aHaXRGGOJLVsoDca',
      'aHavnGGOJLVsqsFS',
      'aHbnwR_I-Xd1HKyw',
      'aHsGsqyzVlZmCUdw',
      'aH0y5IVAySlewi6E',
      'aH4fSVPH99sqqASL',
      'aH50gblBRVt3ULma',
      'aIDEoTiOZ8YEPwEp',
      'aIDGD3pg4cqZmMcy',
      'aIFqhtQiiivMXmvo',
      'aIGm3dQiiivMeEbJ',
      'aIIQ-o_gcxct3cWV',
      'aIJzStzXnGVZ_VK4',
      'aIKE_6Hf8gLFl5ct',
      'aIKYZziyUscJkCZ7',
      'aIK11oLZmDeqXvHN',
      'aILuMoLZmDeqcTW5',
      'aIS1tn8PcKiPOMBQ',
      'aIaDiH8PcKiPjhI-',
      'aIbIEX8PcKiPnloJ',
      'aIeLDsODVmvPzQws',
      'aIfoeqyV6ZwKsl3z',
      'aIjjlPrfCjhNyU1z',
      'aIjogSBYU1v7u7ve',
      'aIrIqPmCOcvFWzZ_',
      'aIuscLkaQxsZZiQN',
      'aIu1k8oO5UKOBEmC',
      'aIwsS8tbfhsno_jM',
      'aIxqOctbfhsnxvU2',
      'aIzdPB26TPL-N6BF',
      'aJCOJfct3HPkf1_x',
      'aJC7jJ0rCVQwYhv9',
      'aJDHFODKR8ISLtS_',
      'aJDlflYS7ln6GQA-',
      'aJEYimA6bBSHThiT',
      'aJF-fII6_LCf8Hmc',
      'aJGV8qujH9za6B95',
      'aJK8lLp1QZHP2sta',
      'aJNwmG5yWlc3uwxC',
      'aJkY9919KnD5hYmr',
      'aJmo-BXwkpo6Uamu',
      'aJnQ-Sr-4bMiZ7kd',
      'aJp_u6XzKtK6bas2',
      'aJqZbo5oqnVl0my1',
      'aJqha0gnZmsevJQk',
      'aJsknQbvzhQrmsaU',
      'aJttKQnmx84ZN6SQ',
      'aJ0GorsFKZW845Kg',
      'aJ3Tw4YDuK1x1_P1',
      'aJ5P4Yli8VM78Q6S',
      'aJ9UVKYyoldZTQAa',
      'aKDmYlAlxVe_avgz',
      'aKKGcTJZVszKp6eU',
      'aKKncRPkQTJdk4tf',
      'aKQUHxNhkN2js63-',
      'aKSHIU7PDTTNG_8x',
      'aKSQsexZ_ujgiPvT',
      'aKUS4Revrvs4up1j',
      'aKUpAP72e03KSVGF',
      'aKV_9y053e3LfdIB',
      'aKYEXeeMSmGgoa6p',
      'aKY2z5iVM6JjPilL',
      'aKaHEv72e03KzLS6',
      'aKdo5XOj2o98E_2r',
      'aKd5gvXMhtnCTjM9',
      'aKg44QuB1fqZRxiP',
      'aKisW6sBbOvPR0QG',
      'aKjGSaAgDMq30p2Y',
      'aKjjsSyptznoeoNa',
      'aKwztq-FflXSpoAP',
      'aKx7k3hrh7DLs1iH',
      'aKz-Q2dGsZB9qthL',
      'aK57sc_ZG5Tgjgtp',
      'aK8jn3L7djCbvJov',
      'aK9Y0pneqoPhb8fw',
      'aK9i02CbROJpQFmN',
      'aK-jelW0CrfgcRjR',
      'aK-mFXj7wRKMS3td',
      'aK_CcVW0CrfgfJNC',
      'aLB2d3aMtikTb9_v',
      'aLDnTHa3zbgSz_5s',
      'aLIg3Kulz5AvylcI',
      'aLNPqAzv8C5FXLmJ',
      'aLQu_cPTnkd89hGy',
      'aLTFEAzv8C5FnfQP',
      'aLTesQzv8C5FpGLq',
      'aLUH7wzv8C5Fr-Jw',
      'aLczcBYbf-dMF1Cr',
      'aLg08RYbf-dMclWG',
      'aLjTjrM28LtuJHLA',
      'aLmyaRC6IghrE2fp',
      'aLo2RYytmLeoWKA8',
      'aLrdh6KyFG1D8bHj',
      'aLuQxJhG4m_1riSV',
      'aL4vdoF3yBL9e6_N',
      'aL7eXm5qJk9RGZcT',
      'aL7n8ujK6_IIesN4',
      'aL8aBg8NKj6DeSO3',
      'aL-N8QdD3Aq3ALUp',
      'aMCEBUCplGjnMqQj',
      'aMEVHNrsL0_RAaDq',
      'aMFWOBtIyIBOTevn',
      'aMGJ5_NO_Tdb7Rzd',
      'aMGSPJUP8pJ7afaz',
      'aMJG6AqvckbyDMoG',
      'aMJUzPNO_TdbTMXc',
      'aMJbRIUYEBfoTfPS',
      'aMP6WTLkPdUZ4_xQ',
      'aMaT4BW4WBdc48Fz',
      'aMdiYjno1p7fClPk',
      'aMggSOYt_sG6rh3b',
      'aMghonmZzMcDfuvI',
      'aMmzG3gGK8Nx7mUr',
      'aMnIhvkCZ5RkNaWA',
      'aMrPudZx4BAJd5fd',
      'aMsIXNZx4BAJkFD5',
      'aMsmWLFdSbQMKyDw',
      'aMyOjqXuEEmtl_Bv',
      'aMyekdQdS60ZVYBJ',
      'aMynpKDo89dajlND',
      'aMy_A9QdS60ZY3zH',
      'aMz39wNfMXyymKxQ',
      'aM1lCfgXZnrpxD0H',
      'aNCWLemeoyJXeF37',
      'aNFNhX4Ac0_VpOG-',
      'aNGJzOKlRBrHPg9h',
      'aNHYuACODYLWbhuB',
      'aNIUnmjfGLpTp4fK',
      'aNIYtGMz1JN_VcNg',
      'aNJIyE_CFoXPR_Nj',
      'aNLItmMz1JN_kiCe',
      'aNLrGRUF6tX6Fvff',
      'aNPDUfYyG41v1fPk',
      'aNVYz5-qlnzRFthT',
      'aNWhkN3GVgcqX9lJ',
      'aNXjjMU9nIilcpw7',
      'aNd0ybNwGRlq89zj',
      'aNgLERKH7qDut2ru',
      'aNrLFsLSDuMJBMnb',
      'aNriH70sBV3i3Psv',
      'aNvA7ZOf7HruaqzL',
      'aN2irMB1a3zC6q95',
      'aN74T9RLekBmM0ZH',
      'aN-EDuS40A_821r_',
      'aOAe7CCmOe47YnrN',
      'aOFDMJIbprXf0qnD',
      'aONqbDgmB5uCVfrK',
      'aOPZT6uYO3oP3NWg',
      'aOPccqBszCzwsoRQ',
      'aOVDOc5pWx_xTp60',
      'aOXMJGDVx5wZktV2',
      'aOXQvjEFxZ3JblSK',
      'aOaYjeVyORQl065j',
      'aObq8VrLOtWmPk2z',
      'aOcNz_Fcrdd0Rv0t',
      'aOgjX_Fcrdd00kan',
      'aOkBJ7iSm2R5JwfT',
      'aOyt10E1wid3qEl2',
      'aOzqwEB-WqlNwHjG',
      'aO0gc8qXM_vuvDkK',
      'aO2P7lWEqwWCGiwN',
      'aO6hSW4Rvh26H4qU',
      'aPAqK236zEkY7Gia',
      'aPB2F6jHzAYNIfeV',
      'aPKV6WKzRpNT9Uye',
      'aPKtCr8yI2N1fCXA',
      'aPT9BvyIKXaZuCES',
      'aPYq643Bh8pSRRBL',
      'aPYrcY3Bh8pSRWPa',
      'aPZ8gP28vA2xTAfP',
      'aPbSiP28vA2xfVTi',
      'aPgVyj4HLnk6DguK',
      'aPhN4b4D8m0rNqRI',
      'aPhWVg48Nrwif7xA',
      'aPk-RNP9gC83eNkM',
      'aPqn-qGp9F36WWDA',
      'aPuCe17iDZPr3bw9',
      'aPvulcRI7aDrjL0A',
      'aPz_hJibQvQkBUmZ',
      'aP6p7JibQvQkXPTW',
      'aP8EOr7z2VPMsk4_',
      'aP9GA-4Vd2C_xyFC',
      'aP9GRoB9mvItHk_X',
      'aP-VuXk2_uLxQB8N',
      'aQADcoB9mvItcSu8',
      'aQDw2yBdX0JvR2MI',
      'aQGIVR7fgtuePMzm',
      'aQMvKHcOVcClhRsf',
      'aQN7idzESRI_m6bn',
      'aQONJ8Wv3zZ4gslp',
      'aQTioeUVv7A-vBKv',
      'aQX4Igg_UvfLGgwO',
      'aQYy2Qg_UvfLIoJO',
      'aQbU0pJWk4jwTpww',
      'aQhojQ6vSbVwKNnK',
      'aQj7cP7CiUqWv50l',
      'aQkQWBhxMMCspN6X',
      'aQnqwSo1Xvrj8eA1',
      'aQn1aom6xtqCxdeO',
      'aQoy9zNjAu0gpBpx',
      'aQq87pZcRaCaGETy',
      'aQsFTpZcRaCaMoNt',
      'aQuMFgUdgkzpPaUH',
      'aQ3YJXeNwXpXo3r-',
      'aQ5hmsbDreg__z3C',
      'aQ-7v3eNwXpXVjx3',
      'aRG_cN8EJTZ5F1PW',
      'aRXBTJaqjfymSRXK',
      'aRsgY7HH87MK8jjC',
      'aSHS3YljQnMvcD8W',
      'aS26y13BTyFaAWYs',
      'aUCaa-4WathAQhvw',
      'aVTqxBvINsyunDfK',
      'aVU0VUNHVwZoMzI-',
      'aVVJ_1Nt9woLQRPH',
      'aVhMNaxyuWyxJUG7',
      'aVsln_YgsZcAS7jY',
      'aV2Wtm-S_klgcvLd',
      'aV2oFYPLkRj7zkIO',
      'aV3NOPuJqDRE2uAp',
      'aV6alXjkKCIIa1ct',
      'aV79TUP58eGbulVT',
      'aV8GSSniALB3GZ2B',
      'aWSpWLmChAtnnTO0',
      'aWVAFTsvzcWnRiXe',
      'aWaJo-7_YPtx3ow3',
      'aWb26u7_YPtx8_QD',
      'aWenGcPKjFMBzVqp',
      'aWiuoYqO1WTlz6_D',
      'aXHBKvn5qAEQtz5V',
      'aXRkmV5ol0GGqpsC',
      'aXSfsUabqGhF-t6m',
      'aXnvb1Uzi0KWHDM8',
      'aX2c6KfK5FAAWUsd',
      'aY0h5Kd2qlJByapb',
      'aZIK3CxdIB5NYfYo',
      'aZXCOgLOaw_3mOIB',
      'aZXfSX-JMbXNrIRQ',
      'aZ3Hp9YWPwWIr2I4',
      'aZ58wZitifM9Bpnp',
      'aZ7VXBbzqRG3LqcW',
      'aaIx8_sOjNoeIlx7',
      'aaUC3F3fbLS3BZ6C',
      'aaUETEhrornfDPkj',
      'aaWoejYoDHv71J1-',
      'aahAqRijxCXX2_De',
      'aalJCeVMgoSLOwee',
      'aa5-a8OlcI_oGPhk',
      'aa7by3YSVVgp5_-I',
      'aa8Yq9MF9_Qrz89w',
      'abHBsuzIv_ET9ppX',
      'abLWRYsDZRVcVYWr',
      'abzE5hXc1T0EaIBK',
      'ab_pO-5HpyXnd6aJ',
      'acOLZJcOWZF0XoHl',
      'acf5AQw5QIKdsRhw',
      'aciUIQo-qaiz_e2G',
      'acrIIPqe84EFHC0k',
      'actdmZf3JZf2coei',
      'actlVj_PoVsMlj87',
      'adF7XQMQpAyMDSu3',
      'adTvY8LrVp1PiGfT',
      'admHUCIgMoV25PYC',
      'admQwRCWoPJxoQ1e',
      'ad1a0D3SABrJtS4z',
      'ad7j-ZoxzDOpsio2',
      'ad_VGeDh_13ScGnX',
      'aeBCcRFMumJpu-U9',
      'aeE70SPYwQF5zO4T',
      'aeP5xTQ7y0ro8xP5',
      'aeVNxLneTx4L-sp1',
      'aehjpCMRvGI7D9yT',
      'aexSTSvErg-uhpQ-',
      'ae9lF_UiFraTUa1c',
      'afKZC3sef-Cn_2Fd',
      'afNAXeNmbxu34wqr',
      'afS45ncpIJiHkwT_',
      'afmzjjU97YQ_6J8B',
      'afqOI05q_2FeXeYq',
      'afwnndUf0ekLyYf9',
      'afycCY3oA8ccSLul',
      'agJiS7sLZPzP940v',
      'agtBslTN88yqoeqA',
      'agtG3ZjMHUGolu0d',
      'agtedBZ40bC2ioRf',
      'ag2pTEWvZrLq3fGM',
      'ag4J4tj_v5nQb_0a',
      'ahOR8W04qFipgrbR',
      'ahSW7norlstJJ8mJ',
      'ahc6Cbkq9UrbuTuk',
      'ahoG-3GHaOVDObr-',
      'ah9v42RsLq5O02RO',
      'aiZ4H7F6vXaKnuzV',
      'aix7pz_ltDMCqk3a',
      'ajCCdaYHiBIMiaiT',
      'ajDuWjPLREO9rCJc',
      'ajJETBrBU9LZ13pv',
      'ajTwbQbmfrhD0Ghn',
      'ajmUZMtKPRDyu8ys',
      'ajqG-jnGTkE4rRmz',
      'ajtv5-C-3edi4rwa',
      'aj4RpL7tx0t1ptsm',
      'aj4plMHEA6NMgDvm',
      'akJXY-r3MP3E9CAJ',
      'akP21xPGYpJFes9Y',
      'akXE6UzofXABmrR9',
      'akf7GKQ5vBJBxZtw',
      'aktIFud6D8JmJB5u',
      'ak0plkohVOo31BIx',
      'ak5O9fhLoIpabG2n',
      'ak82ioZBP-TnuikV',
      'alCTfvGS8-O1ElBw',
      'alKqaLsbbxet2sRE',
      'alUUdlJLcu6_V-lP',
      'alUuX8YUzgZaGlJC',
      'alja4A61-wIlXj3j',
      'alk5smy-tTDTN8-K',
      'aly5jEkY_TPtMSGD',
      'al7YLElAS7NzKj39',
      'al8l1-49m9fMDqY5',
      'al9ytwt01_nR5MNy',
      'al-I7R_b5I4PEYII',
      'amIL2jLugx3bH31g',
      'amKGdG_Fka1porlP',
      'amYnzu2j5EgIQECD',
      'amitPoaiZmPUe_jj',
      'amk1A5aRRPVA7J6_',
      'amyYPlEjfKkaw6mc',
      'anErJPTzDlPftEVD',
      'anExAyey3z7lf-3k',
      'anToP42fzj3N2Ph9',
      'anUPDibiIrfeHRJZ',
      'annI9pD6vjOEJBTl',
      'annh7JoUEWU_aJuU',
      'anpxPTZqvg9RgkUn',
      'an9grUpiFpT3FkQp',
      'an9782VZQeBfwChn',
      'an-Hj-wru42zSrCl',
      'an-kTEpiFpT3JSQ_',
      'an-6sd21nEdbdGPJ',
      'an-9hN21nEdbdKCQ',
      'an_Hw5kbJm9WhDrJ',
      'an_0ULS5qS7HJxgI',
      'aoADL80evs6k12Qi',
      'aoBsT4QeJBSiSNjq',
      'aoB_xOlmG4ogx4IS',
      'aoCdEVeheM3XW3uX',
      'aoCjG5Y8wIcNovfT',
      'aoCkis0evs6k4egj',
      'aoDYnp-sm3MB5tzS',
      'aoD6R0piFpT3OvQH',
      'aoESjOEsLTGt8969',
      'aoGBCp-sm3MB8A9m',
      'aoGUoJkbJm9Wn3w1',
      'aoGe7LNPFj879KGP',
      'aoGnQ0piFpT3RG4e',
      'aoHjJOlmG4og3AZ8',
      'aoHnepY8wIcNtTIO',
      'aoHt5xcQX_yPN86R',
      'aoIqGv56ZMnZHlFw',
      'aoI3WJ-sm3MB-wPY',
      'aoJCRmVZQeBf75kU',
      'aoJQELsf9rtupRUK',
      'aoJYa4uB1XkZzrv-',
      'aoJzUT9fj0AazemO',
      'aoKFaeEsLTGtCS_w',
      'aoKWnZ-sm3MBAnUv',
      'aoKljwi3nXp3MgmU',
      'aoKrTMzH5d4OIEfD',
      'aoKsuczH5d4OIIg5',
      'aoKwl_PTQLkBPnQs',
      'aoK2N_PTQLkBP5BL',
      'aoK8R-sqG0O7PvfE',
      'aoLHvIgG26H_CwXK',
      'aoLIEp_IHGrthpr7',
      'aoLLUr4LT5XXcp2A',
      'aoLyPgi3nXp3P1-1',
      'aoL0oZ_IHGrtjj2d',
      'aoL4bh4FzmoSqqVq',
      'aoL68c6S8gfRGE7Y',
      'aoL-rh4FzmoSrDkR',
      'aoMB4bsf9rtuwMAO',
      'aoMJ59XZHt19WB9r',
      'aoMUfyvzXOnaUkXA',
      'aoMU-mlwCaNDHUuR',
      'aoMYs91xhrga0sIw',
      'aoMjFD9fj0Aa8Os6',
      'aoMzkCvzXOnaXZed',
      'aoM2re67toJA1rMG',
      'aoM5nfPTQLkBXjbj',
      'aoM-jGlwCaNDKl0_',
      'aoNXAt1xhrga5e89',
      'aoN7cZDO5eWBXAN6',
      'aoOG7ZnC3ZqFdL2v',
      'aoOTE3EHAk7gpatI',
      'aoOTWgi3nXp3a4AY',
      'aoObK7sSP8k1Ze6N',
      'aoO4dx4FzmoS2qQ9',
      'aoPMxJeXCiBGB4n3',
      'aoPav5DO5eWBaPvq',
      'aoPdgbsSP8k1bl7S',
      'aoPfoh4FzmoS3xmT',
      'aoPn2Rtw4PtrArs_',
      'aoPpiszH5d4OY8l1',
      'aoP2M74LT5XXs-y-',
      'aoQJlJnC3ZqFhpGW',
      'aoQLQMzH5d4OaT_f',
      'aoQLeZ_IHGrtyeQY',
      'aoQNCHEHAk7gtp5l',
      'aoQh55eXCiBGFGQT',
      'aoQm7pDO5eWBdI94',
      'aoQvv2nPxNXWypbr',
      'aoQw6vPTQLkBiphR',
      'aoQ3cczH5d4OcEj0',
      'aoQ6Ebsf9rtuBAie',
      'aoQ6pZeXCiBGGGOr',
      'aoQ8JJeXCiBGGLgW',
      'aoROA5DO5eWBe7u2',
      'aoRYvB4FzmoS9cbk',
      'aoRaJNVHDho1FfQL',
      'aoRhWbsSP8k1ihXk',
      'aoRmjogG26H_ZNrV',
      'aoRpv8zH5d4OffwB',
      'aoRrU91xhrgaGVbA',
      'aoRxg3EHAk7gz9T8',
      'aoR0XZeXCiBGKdp-',
      'aoSEXtTYTFNBpy88',
      'aoSHiJeXCiBGMCZf',
      'aoSUWtXZHt19sDPg',
      'aoSb3YuB1XkZRnX9',
      'aoStNR4FzmoSD128',
      'aoSxr2lwCaNDeMxC',
      'aoS-zN1xhrgaMIDD',
      'aoTFlJnC3ZqFuSRZ',
      'aoTYY5nC3ZqFvSE1',
      'aoTe68zH5d4Om7Cw',
      'aoTkOGnPxNXW-KO0',
      'aoUCOouB1XkZWraO',
      'aoUaVJDO5eWBqJZi',
      'aoUkYbSHYadut8P8',
      'aoU3Wd-0lTHouCU0',
      'aoVGwPVNGCsPWgEN',
      'aoVJ8DnWR4uPoUsj',
      'aoVMWYHdVrOlYjPC',
      'aoVNmgpi0CYQFWQH',
      'aoVdry8EntozhyXW',
      'aoViF7SHYaduwVP-',
      'aoVuGfJH8zfyUMX6',
      'aoWEsJilfTwTuV3E',
      'aoWHXAV6WsGuFxM6',
      'aoWHXXuWI9c3Wkf3',
      'aoWbWoHdVrOlcD2n',
      'aoWb6_JH8zfyWSDL',
      'aoWcoljHTx4QmHzy',
      'aoWhBuxmRfQku9TB',
      'aoWlFGcPQ_KbCrYq',
      'aoWqLQZjsAnZdQCN',
      'aoW8X_iVfJuOI3TT',
      'aoXBzJ3FVHjMYi6N',
      'aoXJApNbqvUM_raH',
      'aoXMFoi8E0U0vwHz',
      'aoXP21H0iuTzd_yd',
      'aoXUmvVkL1P8yass',
      'aoXX3g1baoFrH4ki',
      'aoXYU_iVfJuOLROf',
      'aoXj1rCFN0wBkOS8',
      'aoYBDZNbqvUMEK06',
      'aoYImQ1baoFrLn5a',
      'aoYJ_KgLVf4wYu2-',
      'aoYRuNf_E4ITQ7qP',
      'aoYa1EtmNtMo0AC1',
      'aoYvaTtZZ-fYEZ_z',
      'aoZJelW5888VfxF7',
      'aoZmc6KPo0blDQ1-',
      'aoZ1tbCFN0wBrCSx',
      'aoaA5oi8E0U05BPg',
      'aoaF0UtmNtMo31NB',
      'aoaVrA1baoFrRVfg',
      'aoamtDtZZ-fYILW-',
      'aoar-cs9PNP6O019',
      'aoasGagLVf4wfoXv',
      'aoasG6KPo0blF39H',
      'aoasKm69k5ie5AAm',
      'aoasLLCFN0wBtAJ-',
      'aoascg1baoFrSUhC',
      'aoathjtZZ-fYIcG9',
      'aoat08s9PNP6O6VQ',
      'aoat15NbqvUMLmKU',
      'aoau-ss9PNP6O9JZ',
      'aoavqOJaerR6YeKq',
      'aoawHktmNtMo5fav',
      'aoa0vKKPo0blGRLc',
      'aoa8baKPo0blGlZm',
      'aobJgLCFN0wBuJ5J',
      'aobLaMs9PNP6QMFi',
      'aobNxZNbqvUMM5Ju',
      'aobkf9f_E4ITYsZG',
      'aobn8viVfJuOY6Bj',
      'aocJDJgN5tM9oyak',
      'aocPbeJaerR6d2Ra',
      'aocT8GcPQ_KbNWX6',
      'aocnV5gN5tM9rpIK',
      'aocrBWcPQ_KbPot1',
      'aoc_nGcPQ_KbSRm1',
      'aodjFuJaerR6mNzK',
      'aodk_EtmNtMoJEQU',
      'aodna4HQ5CEiPpAo',
      'aod1A2jt7Ctv1Yoi',
      'aoebJQOZuY8MbmNo',
      'aoediEjRrJKv88LN',
      'aoefL4HQ5CEiSCbS',
      'aoemZUtmNtMoMHx3',
      'aoeq_Bozf05hDwbG',
      'aoe07Gjt7Ctv4Cm6',
      'aoe_VpgN5tM91_6P',
      'aofcGZNbqvUMdQ05',
      'aoft65NbqvUMd1V4',
      'aofvOIi8E0U0LggJ',
      'aofwuQ1baoFrkC28',
      'aofzGYi8E0U0LoiV',
      'aogHdhozf05hHAGm',
      'aogHhPiVfJuOptqd',
      'aogInJtiiD1WxWQN',
      'aogI6G69k5ieL0nl',
      'aogPXC7YdE7yBzz1',
      'aogfh5tiiD1WyA8_',
      'aoguN2jt7Ctv9D2M',
      'aogxCptiiD1WyjWr',
      'aohEuQOZuY8MhsNL',
      'aohH8LN6SddusMoW',
      'aohL_YHQ5CEiXK2x',
      'aohOREjRrJKvFKh_',
      'aohQvkjRrJKvFZ27',
      'aohUsW69k5ieOwbe',
      'aohVHJNbqvUMhCyG',
      'aohg38zevlMJNX-d',
      'aohqbFFN86EGC9qV',
      'aoh2O9Md7AxKhX6i',
      'aoh2oEjRrJKvJhqz',
      'aoiHf1FN86EGF9AY',
      'aoiPuj5e8KYQteYz',
      'aoiRUExeGzwzGmYq',
      'aoiRzz5e8KYQtqO9',
      'aoib5dRHvwdlA_yu',
      'aoid_Gjt7CtvHOms',
      'aoinNQOZuY8MpVuC',
      'aoizDG6aU0Fkmx7Q',
      'aoi1bUjRrJKvOeg_',
      'aoi5xtMd7AxKmxO1',
      'aojJlAOZuY8MrF0d',
      'aojT0dMd7AxKoHgU',
      'aoj04PNk2kCkpPNd',
      'aokl2ZCmDCe9ywiu',
      'aoknAo3qjzP1YR89',
      'aolB_nmVc0G4R1Ip',
      'aolOKUjRrJKvR6kw',
      'aolTTAOZuY8Mtf5X',
      'aol3oG6aU0FkraCm',
      'aomAI6-kl04ePHsH',
      'aom1VBaPZCZ8Jgc4',
      'aom6o7uPrlbckm10',
      'aom7wataPOdwe9-P',
      'aonFrP83HbCqvQJp',
      'aonJ-lFN86EGPV4l',
      'aonTCruPrlbclC2d',
      'aonXLXmVc0G4UVLy',
      'aooN7exn9wYZRZie',
      'aooRy6-kl04eReEt',
      'aooW8pn9NykvkbKU',
      'aoogo6taPOdwgm5t',
      'aoojTJn9NykvkmiU',
      'aoojfmx_sG5YUzEI',
      'aoo4_FFN86EGQ_ML',
      'aoqF1ymRgzeqYf4G',
      'aoqp-nmVc0G4XQLA',
      'aor0K7uPrlbcpHPk',
      'aosA55CmDCe95v63',
      'aosWaymRgzeqasv8',
      'aosh6XIzT7If-nMy',
      'aosjFxaPZCZ8PRc_',
      'aoskDxaPZCZ8PSW9',
      'aotQhlRp6BKJVUzi',
      'aotbZWx_sG5YZCx-',
      'aotyq1FN86EGVXzN',
      'aot_IkxeGzwzVX5v',
      'aouARCmRgzeqcXU-',
      'aouJaExeGzwzVjv_',
      'aougn2x_sG5YaPqO',
      'aouhvRaPZCZ8R7xH',
      'aovYZOxn9wYZZAdK',
      'aovi0NDZFYVnsJyH',
      'aovjQ2VzofgqPGYD',
      'aovj4BScdGRnJGpJ',
      'aov34ruPrlbcvVMl',
      'aov6qlFN86EGYvqT',
      'aowFtoyCT9XQ7NEd',
      'aowiaRScdGRnLpUh',
      'aowqX5n9NykvwBQo',
      'aowvAZ77B1hKgTQ0',
      'aow08ZTsbqJ5A_Gh',
      'aow1l2VzofgqSjBV',
      'aoxDnxizX7pRHnAd',
      'aoxGZ5TsbqJ5CeiJ',
      'aoxGepTsbqJ5CexW',
      'aoxNBlKmSBzakFmk',
      'aoxSQ-xn9wYZeL2b',
      'aoxWSFKmSBzak-sH',
      'aoxc23IzT7IfJS8i',
      'aoxqNge3siLqqqmP',
      'aoxrfJkmDDGlfQky',
      'aoxvjwe3siLqrEAv',
      'aox2mPdWcvukbyor',
      'aoyNjr9PlbyckfFR',
      'aoyTGlthETgKUwvH',
      'aoyTzTQtLyZ9C3rT',
      'aoy2N_R7Myhxlzr1',
      'aoy3XBqjdwv8zlVr',
      'aozJ8-VgmfZVhcrd',
      'aoz3_jQGJm52gg8f',
      'ao0GkwoSRT9XGQ8u',
      'ao0SJqVMXB_w4Lc-',
      'ao0YzkI-doWCtlpr',
      'ao0nwgoSRT9XHTYF',
      'ao0nzC82Zisoxu1T',
      'ao0n0WVJETXb2zss',
      'ao0qS6VMXB_w48gb',
      'ao0qsmVJETXb26XL',
      'ao0z13A-q5sMZI86',
      'ao005GrLPK5VYKhm',
      'ao01GfR7MyhxqXYb',
      'ao01S-YtaglXWSlm',
      'ao01g2rLPK5VYMWT',
      'ao01t2VJETXb3Wfp',
      'ao015y82ZisoySFX',
      'ao02I0bOj-lf6tP4',
      'ao02XWVJETXb3YJh',
      'ao02dWrLPK5VYPW8',
      'ao02o1XaHO6QtRtU',
      'ao023WVJETXb3ZxJ',
      'ao03DUI-doWCuu4K',
      'ao03UJg1AIBqSOhm',
      'ao04a1XaHO6QtWPN',
      'ao06FWrLPK5VYZjT',
      'ao06sKVMXB_w5lYu',
      'ao08NN8FcjtnFU_V',
      'ao08nuVgmfZVlU0v',
      'ao09CUI-doWCvAnm',
      'ao09cuYtaglXWnt5',
      'ao098pg1AIBqShuR',
      'ao0-R6VMXB_w5vOh',
      'ao0-rqVMXB_w5v_o',
      'ao1AQN8FcjtnFgsH',
      'ao1AbqVMXB_w504F',
      'ao1Ao6VMXB_w51SB',
      'ao1B7wbdXMkPOS9O',
      'ao1DggoSRT9XIerK',
      'ao1D3AoSRT9XIf9i',
      'ao1ElGrKKJKAx7gD',
      'ao1FHfR7MyhxrEP0',
      'ao1HoUbOj-lf7b2K',
      'ao1ImkbOj-lf7d_E',
      'ao1IxQbdXMkPOlTA',
      'ao1ioDQGJm52keQI',
      'ao1wG0I-doWCxJLn',
      'ao11JUr0RiaT3n8Q',
      'ao12zQoSRT9XKmv6',
      'ao2FI_BBzaLrOufB',
      'ao2H2OYtaglXZqz0',
      'ao2Pry82Ziso2Nzx',
      'ao2RBpg1AIBqWYWu',
      'ao2TXi82Ziso2hEx',
      'ao2jKt8FcjtnKuwz',
      'ao2n2eVgmfZVrEGs',
      'ao2p2-YtaglXcMmZ',
      'ao2vuvBBzaLrSNFR',
      'ao2zLPR7MyhxxVHt',
      'ao3Xg1XaHO6Q3psT',
      'ao3ZKeVgmfZVvHqT',
      'ao3ZTeYtaglXf3Q8',
      'ao3byQoSRT9XSjtg',
      'ao3ePwoSRT9XSviO',
      'ao3ta1XaHO6Q5aeS',
      'ao3w8gbdXMkPZg7f',
      'ao3x9Jg1AIBqeVH3',
      'ao3y0GrLPK5Vk3GN',
      'ao4H7yjXWLQKmAgh',
      'ao5AnMUec069Dp3F',
      'ao5spGrLPK5Vp6eK',
      'ao5tUwbdXMkPeZb2',
      'ao5w2eYtaglXmeX_',
      'ao6EN-VgmfZV2pew',
      'ao6ITsUec069Fu1n',
      'ao6TmD4yqujCG0P-',
      'ao6hqARu0k2a-Dyc',
      'ao6jvfBBzaLretKo',
      'ao6piglM97z5HDBP',
      'ao6p_XNY7IRJOk9L',
      'ao6s53NY7IRJOsD3',
      'ao6zY6L-Ei0WCcSP',
      'ao6zyvBBzaLrfRq9',
      'ao602x33ftFE6m6_',
      'ao7Ca0V7FEF9D9w5',
      'ao7KN7l1Y6p0llcD',
      'ao7MDNQWfyXdhYAe',
      'ao7ajtQWfyXdiHoo',
      'ao7gLdGknGHDHmaw',
      'ao7izQwBhciDSU7i',
      'ao7kTybhgTXERzwR',
      'ao7k8tGknGHDH9to',
      'ao7lKoKtBl4KJsuy',
      'ao7mdHk8EBJ6UkLr',
      'ao7m0hZrNhyGHZPW',
      'ao7nMjbgePWf6F5Q',
      'ao7nYLCNssv6tZ6m',
      'ao7oILl1Y6p0nWg5',
      'ao7oWoKtBl4KJ8tJ',
      'ao7xNx6EIp5CdP1d',
      'ao72mUV7FEF9HZGi',
      'ao79YBZrNhyGJcj0',
      'ao7-eZotSmyJZoN5',
      'ao8QADbXpgMB2nYm',
      'ao8RENJHyKnuqFXP',
      'ao8S6BPdAXHiloMG',
      'ao8fVnk8EBJ6Z2bo',
      'ao8wL3k8EBJ6bPzb',
      'ao9I1tJHyKnuulaL',
      'ao98JuxW1jwFC_pr',
      'ao-DZZotSmyJiodl',
      'ao-3JmM0ubbI7k6Q',
      'ao_CngwBhciDfyax',
      'ao_WsHfNhh2PiI0o',
      'ao_eUyv348Qraoj2',
      'ao_hCHMy_XsYKydq',
      'ao_unINZBLbkWKsc',
      'ao_y3XfNhh2PjS9a',
      'apADMMM6mMbZ5nCS',
      'apAD5SRnQfXUgG4E',
      'apAE0XQXz6lGxUX4',
      'apAIcnfNhh2PkG9l',
      'apAJqI1nUoBiGlYA',
      'apAKnNeeNjpfWD1n',
      'apAO3iv348Qrcir7',
      'apATmyv348Qrct5A',
      'apAWqMf_Yj5eetg8',
      'apAhVHQXz6lGybTh',
      'apAor1FQ4NAW8mEm',
      'apA2s0LhTMRyVi9e',
      'apA-LHgDbv7RWCFz',
      'apA_I8CGgLVxdS7-',
      'apBDJomfmvr9FDtR',
      'apBJmKJbFHA4swrp',
      'apBLUYYraj4JR53D',
      'apBNr4Yraj4JSIvP',
      'apBSXVUvrxTHu-pZ',
      'apBbbaZZf-gJH0Lm',
      'apBi1Bbyqxj-ycWr',
      'apBmWWUYQprwRfdR',
      'apBrpYOPuG8ZEJA_',
      'apBygowUm6FOkV-c',
      'apB0Cfz6C1JHavJE',
      'apB5E8dpwLp96Cco',
      'apCD9IOPuG8ZGJwb',
      'apCGZCXKAw0oKEO0',
      'apCJ92Cuvn0CeRRG',
      'apCZZDWK-MsIwVEX',
      'apC41HHsRmxNGR6C',
      'apDAZ4wUm6FOpOe4',
      'apDEimweinBGLqV5',
      'apDUdXHsRmxNHXBT',
      'apDkJ2weinBGMrDR',
      'apDtZ2weinBGM61U',
      'apD5nxw8JHm6w4rO',
      'apEaDpvWwH9rD_CI',
      'apEvBUniH48917It',
      'apE4yiyxsZaGZQvm',
      'apE7mTOAeN_QAV0z',
      'apGmvbouVtsKoLlx',
      'apGqLbouVtsKocJ-',
      'apGtTPa8sdS1EU0Z',
      'apGtufUWh55hYotB',
      'apG7cUniH4899Y0O',
      'WRQEsPrGEQAlbzQn',
      'WRQEsT0fxgAZyhMm',
      'XPTqsjvNRgAayK8i',
      'XUxmGyI4eQAcI3Fp',
      'XVQtQR1jtgAa9z0i',
      'XgSUhIEszQAENyQA',
      'XhXU4UyYRwAEE4h-',
      'Xkz-BE0E6AAEie8U',
      'Xk1nL4w7pgAEEFWH',
      'Xm9ajrvmzgAEFEiH',
      'XnMnBsyDdgAEAl_J',
      'XnTdzyzeQgAEw0JN',
      'XoTqqcb9fAAEzxso',
      'XodiH_B-ZAAEuq_y',
      'Xqa3Q-rWDAAEHloJ',
      'XqusYVxepAAE4L0K',
      'XvGO5pDv1QAEpob7',
      'X3KIP23QDwAEOtVY',
      'X4XIZcB-cAAE2J3E',
      'X5w_wfpzMQAE0QEi',
      'X8XEahEaYwAEPi0y',
      'X_drqRMOjgAExkfe',
      'YAXuCUUcIQAErmrm',
      'YBBu63I6PQAEFtNr',
      'YD5yKtiyJQAE0iy5',
      'YEaEoQfmdAAEa8Pr',
      'YH2LSAYb_AAEAqC8',
      'YJwgu6rM9wAECwSN',
      'YKvi_Q_ieQAEN0AS',
      'YMdFFpr6fAAEyW0c',
      'YMiTQfcucwAEOVN2',
      'YNL8ircSigAEEYIT',
      'YN9xD2HZJQAE0z72',
      'YQLZEWl3-QAE6EPi',
      'YRK4dAw3Yof7aUiU',
      'YROUwMqMQQWuHw32',
      'YRT5PsuppEZZvDm5',
      'YSOWK9DYbwAEmGRm',
      'YT6MlatRq6PeIyUg',
      'YUN8jzP_xAD-UYDx',
      'YUp4zUS_4F4BiAeh',
      'YUqPVlDrOq4GPUfU',
      'YUys3X23faDzc24D',
      'YXGq_ETkmGkY8g8F',
      'YXYvIPt5SVKX1iRe',
      'YZRHr9Y5sTt9jpIk',
      'YZUVtyDIXjROYs7d',
      'YbGZPpVaoEng3sPd',
      'YdnCLQMeH6DJPE0R',
      'YefAQ6cNsV-JZgpS',
      'YfFyLogrvdj9KV5N',
      'Ygo36tvrVOWByY-J',
      'Ygr2y8MJ0F-0exrl',
      'Yg6e0N--CrltzIiH',
      'YhvvoONITzoxA9L3',
      'Yhvv2XKjR1_yKLX2',
      'YioJsUcKWwBY8BWn',
      'YkGy8U3llYHUPbUh',
      'YlE7j7HF-rOuB78M',
      'YlVSrLe1arsQN5Rj',
      'Yle_H1pmA0KrTIQa',
      'YmlVg26saRm7Bedy',
      'Ym_covgmXb-HjkMT',
      'Ym_mwOW50lVtDpr9',
      'YnNL_es5bio1Qm7n',
      'YoyJvUKXvYDtAj_T',
      'Yo8mKIygrEtu7hg8',
      'YpazPBiUKCHUkP-O',
      'YqF4ypITt1gt_wcE',
      'Yqim_EvoEQckmG6X',
      'YsqjKk8Ua-t2CvxM',
      'YuK72D-fuw8QF5-9',
      'YurIcIA7jBV52GPP',
      'YvEsOge7I2x0kYOs',
      'YvNmUW8A5EvVYTVG',
      'YvnHDcOWt9OSqkyB',
      'YwI1HdDM7_VTmuhx',
      'YwPUgQe4jL_8TlWX',
      'YyL3ShIgokbm8GDJ',
      'Y0QQ4qx61Mv-1R1S',
      'Y0j3G_LetH0mQlgz',
      'Y03AXzLd_JqK1xcB',
      'Y1U-NOSUZY7bQscI',
      'Y1iTRC_loGEqjqwF',
      'Y2A-24qjMchlM_ZV',
      'Y2oCd9m3QKcws8g6',
      'Y36J7wj0FbFUNv2p',
      'Y4Bvc7pc0RUSMhdQ',
      'Y4sCyQ18Fu78LhuU',
      'Y6D-lHckWm3h1kHY',
      'Y6k5Hz25hH9722QT',
      'Y7Kaz4p5pnJmQKcO',
      'Y7fa2STb4PvooTQ8',
      'Y7wgiyKcM6ybLCR0',
      'Y8DxbrxZN9lJATp1',
      'Y8WsrEQZIXBnbtFN',
      'Y8sn87gpuGJzP-z_',
      'ZA31jbudcgWBRAPE',
      'ZBp3It2HybHrN78z',
      'ZCR2s80n945QSs4K',
      'ZDQ0Rw3yyJLCizRz',
      'ZDeeBrtTwNu0N5AR',
      'ZDr_V9_EcFXFEd6k',
      'ZD-BnDEgTZVjK56K',
      'ZEASJMXdlQBySme9',
      'ZEm7dFu2KuDZwHeu',
      'ZFOFt5nq8yjVpZJP',
      'ZFj55tJwHqsZRH4F',
      'ZGzrHJuCH709vV5F',
      'ZHqz0a3iLYM5xdQw',
      'ZH3GikANwgvvziwb',
      'ZIrW3MhwK5zCWenb',
      'ZJCdGtYuBEzVrfC2',
      'ZJoXDxZKY3VWh7fA',
      'ZL_dP8KtthWAfFWn',
      'ZMT8ZQnBF-wtUIYe',
      'ZMylGG76P3OL2ueO',
      'ZNJZDh9GUbCzpE14',
      'ZNqPBB9DVAjudE1u',
      'ZOOSUrv7LK_VsE7S',
      'ZOR-5i6nra6tPq5F',
      'ZOTCWUiQzpbWHzyg',
      'ZOWI_64TN7q06Dpg',
      'ZOgc_K_x4aQj-qTy',
      'ZO3zB4A1ig9zJtgE',
      'ZQW3yjgRSHUPkPeD',
      'ZRF8-UeErtLVt13f',
      'ZRLPbff2Nv2wcByM',
      'ZRX6R7ABhZMiAocT',
      'ZS7I0Kdge3WCl0og',
      'ZTBgzaYHi4Aa-_Dn',
      'ZTfAfTnVaP9SGMZ6',
      'ZT5qUcdjJDFPe23D',
      'ZT96cYdFBosCTf50',
      'ZUlXSJSnrwlx3Kt1',
      'ZUoSrtHPojL5fKuC',
      'ZVRU3Gi3bZitKX_J',
      'ZVZYQXLAal4FzIpf',
      'ZVv6yqtlVBmJjuSB',
      'ZV-aeMJAYPEPKO_0',
      'ZXSV6LizlgkyyFat',
      'ZXeUFKpFbtS4yWJR',
      'ZXt6BeKqseJ7J8oM',
      'ZXuCrXua5GDqGVV4',
      'ZYFb56tZPrhPE0VL',
      'ZYMaUEtw_0ANERXZ',
      'ZY7bhu0H-OGylcyt',
      'ZZtlw3_siARXS2o9',
      'ZZuKy5YTS_s_pdj2',
      'ZZzTG9QSjB_WCEtp',
      'ZakBie64YWybvXpp',
      'Za8XvAK4J9Mq8Hhu',
      'ZbekqJIqAXuPX-wH',
      'ZbiKmD6nxs-f1QRj',
      'ZcCAm6XZVEJbuyll',
      'ZcP-KUy8yeR9Ekw5',
      'ZcZsyN0CtAUDypZ-',
      'ZcZ19P89t6pCZhjY',
      'ZdO9PREJ9DnnlUA8',
      'ZdxoIg0_Umui3SSX',
      'Zd9oGyEDkkVCwpru',
      'ZedLqlmHlmA3NwnH',
      'ZfStjETWxf9Rmsb1',
      'ZfXdfbx7c-xCzsP6',
      'ZgKXwEzeNrlwQBzC',
      'Zg1q5o17mVSWw6oH',
      'ZhQrWxUvFa3K_6aN',
      'Zhj5a1cQTBHJq1RS',
      'Zh1MYP-ZUN1YIci2',
      'ZiHIASPVPH4q7yvU',
      'ZiLldpr1omQg7oLY',
      'ZjP0EGunaNDUhXQk',
      'Zj6e2nzavSgbtQRK',
      'ZkEyWP4mQJtM4d4p',
      'ZkSQXYgaznyxL3rP',
      'ZkSQmcWjiieo953_',
      'ZkSQ3hj-sMvpFhoM',
      'ZkSRFGc1JtzTyz-J',
      'ZkSRQWcnN1VQXwHa',
      'ZkSRntqefJNx7H6n',
      'ZkSWRBi9ZPp-1KKU',
      'ZkSW51zhXwsTk5k2',
      'ZkSZ4asB-gScfsIw',
      'ZkScHfeoUIM02t39',
      'ZkSeXfirqq-TalXv',
      'ZkSf3sQTWEiOr8B6',
      'ZkSgJjSbKV8_cQ68',
      'ZkSgRk7sPuCQEdDk',
      'ZkSgYKsB-gScf_4C',
      'ZkXbdCH4-UFZbxZd',
      'ZkXeFCwEn2md8HYi',
      'ZkXeazBPyA0mKUj3',
      'ZkXeoyTl55f-3Hjk',
      'ZkXgoq3M3WwRjq4B',
      'ZkXgsK3M3WwRjq_y',
      'ZkXgva6sOLLi35YF',
      'ZkXg2PxwQyiJrZhV',
      'ZkXg5T3ywEvfvr9j',
      'ZkXg86sB-gScq288',
      'ZkXh0Fk3PN4NCBPO',
      'ZkXh4CwEn2md8Qkv',
      'ZkXh8CwEn2md8Qr-',
      'ZkXh8iwEn2md8Qtq',
      'ZkXh_q3M3WwRjvSR',
      'ZkXiDZAWU8o7hCt3',
      'ZkXiG5AWU8o7hCyZ',
      'ZkXiKyexBBPgqgjz',
      'ZkXiQn-OrTgW-HSH',
      'ZkXiS1Px-1JjjsRh',
      'ZkXiYkXgdsPb2fck',
      'ZkXicZ49Mp9mG8BM',
      'ZkXif9492OZLkAAW',
      'Zk4-5_R_AT7AVsH_',
      'ZlbjYKVEv2i_H4YP',
      'ZlgR28aR9lMfZB0A',
      'ZlhZAkA4V4B0hUUf',
      'Zlha-hqEI3FJiv0a',
      'ZlhcAxyU_sUDZyF7',
      'Zlhf5UMpNV4GVWfD',
      'ZlhghUMpNV4GVXdX',
      'Zlhic8RVltp3UgAf',
      'ZlhiwheRaM2JUans',
      'Zl_F6awR4yDIatN6',
      'ZmWG73ERsCZXYgcg',
      'Zmg6cGEon-ZcfRff',
      'ZmwNo00Yr5KAU3ex',
      'Zm2hJOIlCrvznugO',
      'ZogIgDMC8fWGNE29',
      'ZpmIjdP9Mt7wQMp6',
      'ZqJQe0Js_-vP53pd',
      'Zqm9Buy82vX3fc3Y',
      'ZruK07xjV3G8rKrp',
      'Zr0Hn3tplRtHRqo9',
      'ZsMb9ypI_BUbLhek',
      'Zuf7YWPvpZAISVSx',
      'ZuivF1IL6Whi3vUu',
      'ZusPRHkUls43Jysd',
      'ZvF_1hkePBaP1KNG',
      'ZvwTTRCAaRqJ3KUV',
      'Zv7rJ-RFX6wThhqK',
      'Zw1govurk1i78y8O',
      'ZxGd3Oa_A-gZbIgT',
      'ZxhhNIwPM4iC8VEe',
      'ZyAukp_4-Z-rpArp',
      'ZyO5JfxauUO-qF3-',
      'ZyPfjZ8cgll8PhqO',
      'ZyULbwpW0IZZ7S9y',
      'ZyeAXjVaKtSzIDSa',
      'Zy0XeB4khraH3m4w',
      'ZzbHV5WixKcNEq6N',
      'Zzf0fce-Up72zPS4',
      'Z0Xq1Iwmu5GQ3mue',
      'Z089qHWV16Iyk2Yc',
      'Z1Cx1-ZJqnN7VKl3',
      'Z1sZMukQxjG14r-T',
      'Z2ajbg1H_emIN1X6',
      'Z2fLWQA3sUAVeLNP',
      'Z28vkGpODHrUl7lS',
      'Z3HJwznIToHEOqSS',
      'Z3HdiznIToHEOrLO',
      'Z3MG6DnIToHEPM6N',
      'Z3ZXD3f3YuoEvPLe',
      'Z3bofTnIToHEQjk2',
      'Z3c2nQ__P31aJQHx',
      'Z3ul1m74M7IuQly_',
      'Z3vzuOkZUgXJs32Q',
      'Z4aBBJau-zEOJKyC',
      'Z5EYfpWqB494fA8E',
      'Z5JzFt8T6QbbLYSb',
      'Z5J7mc4CsiFDkZPN',
      'Z5ZJdCos7AKjY-Mu',
      'Z6Acay2O2gk7kQ7E',
      'Z6JbClJTCbU5znwe',
      'Z6SQodylI4YxLtEa',
      'Z6in23VFRxXUFpcy',
      'Z6tzd2hxO9NBvlkT',
      'Z6t1z0_8rxilyC6r',
      'Z7vinyrXilLyylZA',
      'Z7ydkfvIQllHo8_h',
      'Z7zTi5PtO7ekOA9R',
      'Z8MY8VwXPrX0MO4p',
      'Z8XRe_iOkcAK21Vj',
      'Z8njsblvH_-RuRmO',
      'Z8uhKHEnHXva-Mgp',
      'Z9CWGxLk27YJCq5d',
      'Z9x4R6JO7GUJlBbP',
      'Z-F5BUr7P0PB9RHY',
      'Z-Z_d7RJc74imG88',
      'Z-aVcFzg6PLyAk2m',
      'Z-f5LwG1Oa4wI741',
      'Z-xDlLToKjyjVKcD',
      'Z-8t2cRLw8s0kekm',
      'Z_XCg7kfJdh3lUA_',
      'Z_pZEGDks2_N0JVN',
      'Z_59aZvPgH7PuXzD',
      'aAApZknkla08E216',
      'aAertLY68FqpIkll',
      'aAlU2WGZNRQ2xl6f',
      'aA-ttd8bQIM3xyIT',
      'aBDRecSVA_X2wxDZ',
      'aBHW8VXt4i5QWp9j',
      'aBRlRYEhihsPpxCZ',
      'aBdiyTkuw3fmY5Zd',
      'aBuKo7c8RXQUu2Cv',
      'aBzHvuE1fNy_OKgg',
      'aCLy9bTjtMdaGL8W',
      'aDheM_p2kInGlQFU',
      'aEAMVb5bpaViOsgB',
      'aEWx_4XkDZn2F-W-',
      'aEb_P0VT8ukLTeCU',
      'aEiY2PnlYw2mS0_Z',
      'aEslf3gS-JT_aNBu',
      'aFAysV5mpv2MqEU5',
      'aFS3Q3-sZFiwGU_b',
      'aFdCc4CJPi8j5uqb',
      'aFzpN5adcFpa69r9',
      'aGUttDlBfbtc-GFV',
      'aHokFzMQggEJsG1U',
      'aHplxzMQggEJ23is',
      'aH9QGtGannD36xsv',
      'aJYJTx0-oPmg27tL',
      'aJYMHojpWFAtyPZL',
      'aJpizNykUy3DLcJb',
      'aJtKNFDsDo-qgFZD',
      'aJ-XpMuH4cdQuRqu',
      'aKLo7pJSTwWzX2Gx',
      'aKO9AlXFOc4MW5qF',
      'aKXdS9fzys1DOJZ7',
      'aKeFLeoN37gZ_u8a',
      'aKjc1h0heSmuSu9f',
      'aK1sE_HpPlZb20xe',
      'aLBUt-QNSbIMNoQi',
      'aLBucwPFDYg_6o6u',
      'aLEFyvyVZwTcKFTU',
      'aLEtlh1ehDl-jZ4e',
      'aLHLSuhsOPrsWbf2',
      'aLUn3Azv8C5FuUiR',
      'aLemmBYbf-dMQFpf',
      'aLs7HBVwBvtoXmJN',
      'aMwtTFKNatM6XIz6',
      'aM2btAyUNivlI5Lz',
      'aM2oTNjzPOb-a14B',
      'aM_uBXyGYWqGiI0x',
      'aNI8XGMz1JN_YKTj',
      'aNMdLwU3st4Ep7xd',
      'aNVINqQjGg3i-qbI',
      'aNtol0y2yOOiY2vA',
      'aN8bbvjVOo6Lo-ld',
      'aOPx_UNxQW_pokfN',
      'aOiYZsRVJOuZG8_R',
      'aOnl2BQHe-MfvaUp',
      'aOxDc0B-WqlNay14',
      'aO26L928YJlxfkDv',
      'aPKDX6jHzAYNK98e',
      'aPlPlQ48Nrwi5uzP',
      'aPvYzqGp9F362tuH',
      'aP2YyJibQvQkIssm',
      'aP8ss5UkvW9NJiA2',
      'aQFIHzSrxxhBARSw',
      'aQXh13KCtd0CJuif',
      'aQsPPJZcRaCaNisC',
      'aTAtMHNdGGVjiaVb',
      'aTGguEoaALbBPPPd',
      'aasvrjZDTG_HUqBa',
      'anRNVFJO7-Fiuzwx',
      'YOLtV_IgXwAEUN4k',
      'YeYM-OcT8Toh4qYy',
      'Y3NRhknNgRkcnkOE',
      'Y6t03ur31kBCM81T',
      'Y8XUSlPYeDhQmGMK',
      'Y8p10zlGfZpCyRvy',
      'ZKORfdFJA1lSrAVB',
      'ZTmtQfiLe-XaZJ2n',
      'Z1tQC1o4OlJo-l5a',
      'Z63e35FshrrL9qBc',
      'XFi_DLsVcgAkAFBQ',
      'XdJ1Tw-j-gAEjuKP',
      'X1jsI6OdNwAEyA2D',
      'X5Nr7GELJQAE9BnV',
      'YawVZGjg76wY7FEa',
      'YwL-6d_2cZAdneDI',
      'Yx0gm30-L508l9UF',
      'Y0Gi08i9Czx00yYN',
      'ZN5IVxman4o59Rtn',
      'ZZ-dUCi90XpwEBWx',
      'ZacQ8GsI0pAfOgPJ',
      'ZyLKeLGt95UwavD3',
      'Z5EkvUUwVg3hrJRf',
      'aCIK9BuuZ1DqK-2H',
      'aCeEl3gtAMgnRCBZ',
      'Xg_zCI17nAAEkbD2',
      'Xms4iFqBgQAEjLy2',
      'XnMsa65FxwAERlmQ',
      'X1SC58yAaQAEjFRV',
      'YILyQfG3YQAEidem',
      'Yk1-AihXJ_h7jrGZ',
      'ZBt6jPrJVQMZQN3n',
      'ZYTkc7VzEng7yxES',
      'ZnX55cZhBaIJ1r_6',
      'Z9w1eGAxkQf1H7uf',
      'aNUtCqCNXJoYNhAO',
      'ae-Nzmq1pMcQ1QXw',
      'aftKPakZitrqmKru',
      'anDRNI-lv3GkUmeS',
      'anJWcdrgzuTD9XGG',
      'XbCyA5X-kgAZNmef',
      'XnFHlAmZyQAEDMCd',
      'Yem_4aPaSYRsNUC7',
      'ZV4BLAkISnBrxb4f',
      'ZuGxE8bIIYXxrNjK',
      'ZuNTyelTM8ySysTL',
      'ZvQ2OURPukD02AFG',
      'Z8ChRFwXPrX0fcnS',
      'aAElUjf-egNBJGbG',
      'aXo9MtaRb_6G6WpX',
      'ad0N8UxUj4HMQOY-',
      'ah6JDafXPafdVk4p',
      'XgaVW5A6kwAE_LO1',
      'XiHh3t7-hAAEZSpd',
      'Xm7Cz1i2ygAEZ_1Y',
      'YQh_g6r9UAAEZCB3',
      'YQp-0vR-fQAE3Nff',
      'Yw-mXl4AFmhF6Mro',
      'ZCQLTPGWQ5vWopPp',
      'Z4nseWqUav4_B2Ke',
      'Z8nlUiph4bblel06',
      'aNvO0y-RQHadxUCV',
      'afybqtm8bd9Omz5l',
      'anVhjYF72dc9fwzX',
      'aoeBlmcPQ_KbXRfP',
      'XYd9gybFhAAc2VGh',
      'XkwHnnJ8pwAEvabL',
      'XmpdWrboKgAEsUrr',
      'XqJa3WR_OAAEovRA',
      'X8tFJmre_gAEUeBJ',
      'YcLEvV3LPLG3bpPU',
      'YkRxwrrTqh6eAp1p',
      'ZEwqW91KBECSQkzC',
      'ZxBEO7oHd9Fx7m6W',
      'Z4VHmXi1ki64Yt-S',
      'Z4v6E-KoPMLdbrJ8',
      'aCQ-YrqZJCZeCCxT',
      'aH50GUWYrvApWo89',
      'aZh6X-izT-IzEQXH',
      'abuJB8vFQSshSFPf',
      'aek4OwTs3O8E2aS6',
      'XYmeLfOvTwAg-et0',
      'XdZ0ixYPGQAEBOKY',
      'XgXe_5V0qQAEs83t',
      'XoXxf52lUgAElaXj',
      'YWRf1au59yMGlE7I',
      'ZCZ-1avrHdPzsCBX',
      'ZNDCqww1uQjUzz00',
      'ZPl6zFdvsgsXeolw',
      'ZR7sQZX0nvVib2Is',
      'ZZWN_yG0pqySI3Fe',
      'ZdgZkoae-AiunL2A',
      'ZeiMns1n5PAnqOE3',
      'ZfsDdcbndOYUtgC8',
      'ZjLXnfl4rxXpzdE_',
      'aNKH09pqQPMl7w6y',
      'aUOe_WWLxlcrNJ05',
      'ad0p60OysfSyo1mG',
      'Xii9W7w9MgAEfPpk',
      'XkHYTQj4UgAEqFvz',
      'XnrZO8cdhwAEn3zQ',
      'Xrmckz4CqgAEiUa6',
      'X_dTdzmmswAE3Fc8',
      'YLjprSzGUQAE3DGs',
      'Y5I78FfkzcFtRHC1',
      'Y72np4Yk7C2qC7zR',
      'ZJxwVsEpHOLasuWo',
      'ZRuk-091JxC_ouEp',
      'ZVhKWoRyeUqjAmQK',
      'Zit7_6jmiuzvlw9F',
      'Z88mPYFunhIF6sIz',
      'aAA8sMvEeZOxX8k7',
      'aHkjv2YXkreQ62kG',
      'aVvXtucv6Ot9QNIQ',
      'XymqDpEQggAEseAy',
      'YkHRuasILW56-Noi',
      'YrRL69-jvuEdVnMj',
      'Yww0klnz5Qgc2MWt',
      'Y48_u9fjfhOY0VOa',
      'ZCxF_a0-hTnuMi6K',
      'ZH-bvMWTj46ff8Tt',
      'ZSsd-VG0HTtXQVG-',
      'ZV24quhYhxTzhVy-',
      'ZhbHxwWFQBAyidRa',
      'Znl_wSB8M1yrd_AO',
      'ZqPlXYORKfSMOI51',
      'ZwxWd3P0tsmuyC0p',
      'Z08y5yBbRd048lLX',
      'Z4Zyh2m1oXrt3hgS',
      'Z792najNk1PZwwSc',
      'aBkRhe58NDHgqz-X',
      'aTmxzSxdFcoKa7sY',
      'abtJt8KI53VckdKN',
      'adXwWIO0vvhiy08b',
      'aj3qub7tx0t1ocHQ',
      'Xm-N_14niwAEa-x-',
      'Xpz-bBZoFQAE747N',
      'YJKl7iZD4AAEnz0X',
      'YdTqckjO6cnBrGIs',
      'YeH65CVN11WltykA',
      'YeMTEM6iia0QQS2H',
      'Ykl9AbO6LGcB_3hq',
      'Ym6KgnGe1GkdisbK',
      'Yr01dspdlFJwGox-',
      'Ywk0qUNuuDTVy_Rl',
      'Yw79iq1EFt5dKeve',
      'ZZ0qFWXMPkoBetBZ',
      'ZeGkTrEso8DqC8Ds',
      'ZmA2n7wn4Dbg4ZV9',
      'Zmdf8oSoBqjxPlzu',
      'ZmrqGhS6f_Jsl6lc',
      'ZqCdkbjy4ISNQ0ZG',
      'Zw9PnHg69jnAxH3c',
      'Z34mVIA_e_i3tV9j',
      'aEsEv--bGJQqjAHy',
      'aIPKkcZZkoFwlBWl',
      'aIjlMv2ONvzYtshK',
      'aarVwfXLF4gHDqhP',
      'agF_ltJWauQH0tId',
      'aiBHTGwAh0kbZL4V',
      'aisRRx98dtRLhLZd',
      'Xtan39BmEQAE9rRg',
      'X0pw0PmCLgAEqNTi',
      'X33Vb18IVQAEhJeL',
      'X-DNEzZp-AAEcI0p',
      'YRKmVLFTPESqOT-a',
      'YS5a1eunSnzLGJ5t',
      'Y0nRaoLj7JlhaBof',
      'Y7YhdBA_-GRJ5hx9',
      'Y8_0tQwqVDzfFJpB',
      'ZOQCpHdcfdxCxCxa',
      'Zfo8Z1J7tE2VyJQd',
      'Z9MGvpVivXYt8Sqt',
      'aHZ3eWGOJLVskmvF',
      'aTMltjNrZ99rzlJB',
      'aYpeTnEWf_2DeVia',
      'aY5DfzM8yyrnq-fl',
      'anTq14F72dc9bHeh',
      'VnIqy8Bl4w4AXeLh',
      'XIHHdDZlTwAaehG9',
      'XOR1WSRPUQAZUNu7',
      'Xmq8XWePGAAEm241',
      'XpNVBJrmnQAETFj8',
      'YGD72GaesAAErj01',
      'YJThq1hOGgAEnmDq',
      'YkkdX3LZXlqdDzz0',
      'YrYQd2DiILo1SM6N',
      'Y951S4Qtae5w6hAm',
      'ZiJw5Ya6emTstJ48',
      'ZieK87KccBu5VVb0',
      'ZjvGArAiBUq3na6F',
      'ZuzyJ0EhRzO75nn7',
      'Z3xKdOkZUgXJtHhG',
      'Z5HaPVCJeHSEkO-f',
      'Z9hOWXP1YyHVceMY',
      'aJuVguLBJw3F3hht',
      'aM17cwafx_W28n4B',
      'aZ11ChFql3tvVkxm',
      'Xv3rpidvsQAEsblJ',
      'Xz23nlMpUQAEtShl',
      'YDiP_ZRnPAAEtRrA',
      'YG3tuT9bOQAEOO2k',
      'YMl2EGu7fgAE9iwU',
      'YfJCT88SoktQVzaA',
      'Y8D6VJhfxNmhRAKt',
      'ZZ0Mmwce0AFGEe87',
      'Zoy2NTyyhNs9wa51',
      'Z1yNJ1o4OlJo_axF',
      'Z3_EC1QsUcvBBNdt',
      'aJ3sE_jjnIecEXNM',
      'ahXDyzE9dVcogibO',
      'ajqXaPxJr460ms2r',
      'ajzNZyBGYFgmevxN',
      'anVxsM86H8F_LOHL',
      'XoqCfpppyAAExYnN',
      'YCauxefigAAEGWXH',
      'YNY4FhQ9YgAE92GF',
      'Y0gRmtbNPR1cceHi',
      'Y_6nF4GvEC2_cRfg',
      'ZLGdaNjgy0nDHbbr',
      'ZL3hJsE9h5BFT7Gy',
      'ZVDuI4oYacjqsncx',
      'ZYsQYjtrSyppZ2gc',
      'ZqPhjLcBqK9E26Yi',
      'Zw8NPK6e1shF1KY6',
      'Zxr1jg-chASdeMwg',
      'Z5jiljT7Tqyt04sw',
      'Z6IW9o4BfcdywUpx',
      'Z8Io7FGF1_uEWXFp',
      'aRvI-j2lMqXq1lfG',
      'aSAR5UkvGty3jTDP',
      'aXObMkabqGhF3WDE',
      'afEXHjoEpZN5wGkU',
      'afYM84fehjT_zMEO',
      'aiK1QuSHWyJMg-9k',
      'X2nzbdsBnAAE3ZhS',
      'YJq--L170AAEpaIy',
      'YjnUJedvDdAuybtj',
      'YjpfVM6tgUqIsQ3y',
      'ZBoI24FAGLJ-g_5s',
      'ZFDrTvt5QwVQTfs3',
      'ZIycQ__l1i75EzD_',
      'ZQiXWImCJBtpqJwI',
      'ZZHMcD9xI_dEdiH8',
      'Ze6gQH-ji_QO08of',
      'ZjvPC_19BCEeqrHq',
      'Zl04YTCnUBiMRVLw',
      'ZnqxlNUsFm0eV8SU',
      'Z4fDlmqUav4_B2G8',
      'Z6s6d0_8rxilqt4W',
      'Z_zdjCAnWsug7_pj',
      'aRdePn3eif8DbeW5',
      'aleChg61-wIlFkN3',
      'XKP7mJJpYAAaV0XB',
      'XkaWo11fRwAE4SxA',
      'YACHGJFQ1QAESUzj',
      'YfgBpP7F5Bd7sNzZ',
      'Yq-vk0cUUC0UCrjE',
      'Y8AySeaserNSuF16',
      'ZKPBZf6j_vU7LW5z',
      'ZMq7iIh6kBgTv6M3',
      'ZPjvTg8htfJ5hSOQ',
      'ZZR_zMeJpvk0tXFc',
      'ZdHkpwqWqc8Km28v',
      'ZoQlYaerZ4aUKdnQ',
      'Zt9aWptr2n5Med2a',
      'Zust-UNe4fwkTA3Y',
      'ZvkaiYlkvr2k1wmc',
      'Z3bVJnf3YuoEvlJ3',
      'Z7fMU5xoZc1d2Zak',
      'aGPnemMf8Jfqr2_i',
      'aY8FlR341hqhBTLL',
      'ag2ISiFaUJlseTc0',
      'ak7tNvVq6H5M8ldd',
      'aoVqeuPaltFP3urT',
      'XmvxTZQrAgAEqtBv',
      'Xuop_UDz5AAEyYV6',
      'XxiJwmQwXgAE13k1',
      'XyKPkL6P5gAExoNB',
      'XzvqSdyPEAAET1IN',
      'Xz0bZSfW7wAE-Vct',
      'X35LkeTcvQAEdAUj',
      'X-TY25xFWwAE6l-W',
      'X-TnY9wBNAAEMNH4',
      'YPr119QSewAEKpB5',
      'YZ60-57iIC2J9Gvv',
      'YdNqmPqbZzd5O9Yv',
      'Ydc51Twz5YUG83lV',
      'YkHVBexBdBgdXAg8',
      'YlREMMysKP6eZ3Wm',
      'Yw8GRI59pOQLCXKF',
      'YxnvbU2agDroQseZ',
      'ZgFbJNlddVBzXkQB',
      'ZjNhdO984iMpyL-t',
      'ZoRLJGwi2TkalTnZ',
      'ZuCde2ApIyOQh7BQ',
      'Z74aDSrXilLycUEQ',
      'Z8sgmYRWBenW-uGF',
      'aaoq0vXLF4gH-M16',
      'afH3MiM3NchZpfjx',
      'XPZzuJAhkwAgxxxx',
      'Xn432CyFzQAEspt-',
      'X0z5mM4H0AAEy9VX',
      'YATcGkLmRQAEgx86',
      'YD7vyZ3BogAEhOkd',
      'YEXsd6PW9AAEsi7z',
      'YWboYw3jqC7PibAj',
      'Yl1x5RnrGQiFH_ik',
      'Yrsyd5IOAgGXt1ha',
      'Ytk9xgEng6Sn0uox',
      'Yv077M5zasrlSa0s',
      'Y5lO59xgWr9nX8sH',
      'Y7RxmcPf28Iqdawy',
      'Y-XtajqDlIv2qGdp',
      'ZIwpVB6LIKTl1_ZV',
      'ZRW1Q1POekJm-lJX',
      'ZS1BUykFKdruwVLT',
      'ZcEAImqQKdDYe3eq',
      'ZivritwmBNx95vo_',
      'ZkfkV0w4JajwWK3a',
      'ZnCv2_zedveLN1Qx',
      'Zv2G0nuUY50-yHEB',
      'Zy0tN2ZGUDMNgqXd',
      'Z3bFyjnIToHEQeXi',
      'Z9SXkUIQVV706jQ8',
      'aOAbnZIbprXfgszF',
      'aRJA-Srqrp1CuOT6',
      'aY0TiXjvlRIgsN0f',
      'aaBcRkXH5l9Sft1E',
      'afi-4Qsb2FwHmF6_',
      'akNaz-cMJGYH3jNp',
      'amO-q3tCvPalgRf0',
      'XnG9kUp0MAAECv5t',
      'XnJBYOBS6wAE66LY',
      'Xt_vrqRiLwAERLL_',
      'YWmwLe12oRRGO2u6',
      'YvPpI2Bmo8eiDF3n',
      'YynJq7E4K8Oot4Zk',
      'Yyo7sA8Mqs_RU8EX',
      'Y85AEm69AjLO6fck',
      'ZG6RIHjmW_CVXket',
      'ZHpEFhemyAbvRmS5',
      'ZPuqAXZoG66LVHo_',
      'ZWZ0-r8Xh8UVAJ2P',
      'ZcZHKH_4kga7Uxp_',
      'ZedY1q--ri2Ownnk',
      'ZljHvdctbeOVWh6L',
      'ZsgugU0eo-MCwoc5',
      'Z5KJF84CsiFDkZPR',
      'Z7f0S5xoZc1d7w8h',
      'aFVFrUGWe1faO6X0',
      'aVgWxJTbF1Ybd2Ci',
      'aWSeDMHK2MzuJf99',
      'aWpBoQ0bYre4VQXy',
      'aXsame9Ni0hzQM1b',
      'aa8TcXaUwPaXZSdt',
      'ahOJObE1l7-40Qr_',
      'XVyCkb1zewAZsnfc',
      'XoxZEEWzsgAEaTH5',
      'Xt-RGjFx1QAEzEM9',
      'X2ULBnzNowAE33kP',
      'X70RtWYh5gAEILCu',
      'YKmzy7id4gAET1kQ',
      'Ykc-uZSBR8KgBbqW',
      'Ym99Iye_6gCegvYz',
      'YnSYFvmAmOHPn-yt',
      'Y2yVAQnlhubmM9Wg',
      'Y3xyfdeqSC0O8tIj',
      'Y8WhM7c2mYQZH3qp',
      'ZFJx6KFUZX-T31_v',
      'ZHlSZXIiN8ZMMfOH',
      'ZJXGYzsTuiVdY0ob',
      'ZT8wCT36bgWN_-i2',
      'Zac4NVUXyE0xmsDS',
      'ZajZ-XJuZ4Hf9Nep',
      'Zb4buIbApoqbYpBG',
      'ZcejgkdXqkQpheu2',
      'ZdeKI_dM0Hpe1pfK',
      'Zh1rR0OU7Se_VXIO',
      'ZqqcR7wSk-euaYDk',
      'Zy4sLHm7ViSy1nIt',
      'Z45N_TOpyBScpxhr',
      'Z6pIKWHDGquJe3Y_',
      'Z8nx53oz7RxRI47K',
      'Z-a6WGay4sr_RVE9',
      'aLpU0ZhG4m_1IdDY',
      'aTCsbFpc543d1fSH',
      'aV7r3CH91rsvLkzx',
      'aXEMjV9hQYr84yWj',
      'acFaBhiqloYhwaXd',
      'ad-G7z0Kts0NIZjM',
      'aelZQshEOm_FciWr',
      'ahQLp7WHYhgSxbYu',
      'aoP99R4FzmoS46H4',
      'XlKJSCDgKgAEp0ak',
      'Xmv3PEsU5QAE4Jgn',
      'X1_s6CEutwAE0QvD',
      'X6khO_ScDAAE4kbL',
      'YAhgibAfMAAEPJ3i',
      'YIG9Xj_gqgAEma0V',
      'YIX4G5MdQAAESBZj',
      'YJr_ogsIMwAEW4a3',
      'YN1utPBIqAAEPGvz',
      'YVQ1e1YTCBR6AsVK',
      'Yf68i03hm8IW6GtO',
      'YwWyAfLSbtT2PFnH',
      'YyjoWOA9100XUAVo',
      'YzwUm9vw4GO2adpe',
      'Y4-svXq83EneaRW2',
      'ZGPrpfh4MD_VRZzD',
      'ZJ7rjAUasbgJMK5m',
      'ZKMli1KOAVa0MZEG',
      'ZKdavi8wT2G67uXQ',
      'ZMva9IxongzdSftg',
      'ZNJCMLMHww-wQG5f',
      'ZTgEFoBSiF2zO6MD',
      'ZUz1ANJkcrKjwx-W',
      'ZcY4KvGQfH5odFR8',
      'ZfGYpI53vDlRxmP1',
      'ZkuQDp1cyyh2hCYx',
      'ZlDRlbdeazd7NGi7',
      'Zoa-lSJhIw2lydk3',
      'ZuvaFZ94d6o9KLt4',
      'Zvr92dBuMUHrquPu',
      'ZwTApKFF6Dv2-4-f',
      'Z1goFc3tkTo1cyzd',
      'Z31mXOkZUgXJtxXQ',
      'Z4Gx7MVcT9em2_nn',
      'aCyhCKagz-NvpsB2',
      'aDdBLSduqkuJQUxH',
      'aFaEN6IWfQc0bd8s',
      'aHVIlx_I-Xd1aFaA',
      'aIyuyMa29qF-ZCZo',
      'aPq9O6Gp9F36YmoY',
      'aP-uUVAEB3g1EZPZ',
      'aTGXp2uDFzcPq3g1',
      'aW2YhA4gpOHLkfET',
      'aXiasPZP81W6qDnm',
      'aY3uWFpGKsvkF9EU',
      'ajDnAit9uB64dUMs',
      'ajv9Y1JHNY5db7Yl',
      'al5Ix7mKjQ6dNxy3',
      'anlVKPKBW-Lk9vQU',
      'W0QAPz_26wB9ZPhg',
      'XGGWWYr9_wAaxxLW',
      'XkGRNHpUAwAEyQSf',
      'XnODqxkf7wAElWyX',
      'XsKakBpzowAE7-Fd',
      'XxhepwLWtQAEGKiL',
      'XzPMS74GzAAEOz8O',
      'X7v9SNxHMQAE4_gz',
      'YNXfNSBaIAAE7Wj4',
      'YOVZtIyBvwAE104r',
      'YelzF5SrUS2g5v-c',
      'YjoulFSnoBgyaQHi',
      'Y8XlgEMZ_L3hPoS5',
      'ZBcg-TwtrXWW5HZp',
      'ZH6qjImu1DLwAelv',
      'ZIpIK4JYDTnUgHkb',
      'ZLai7GUMPLt3ZVqx',
      'ZO6no-K3HWVPG-Rg',
      'ZRbT1RRuGPdcXfX2',
      'ZS5DHoIHw3bl25rq',
      'ZWNgNToocO_Qh4cy',
      'ZgKVWHe4gWbcGahw',
      'ZjmV3cDFj8MlE80W',
      'Zvw39XzUgxCYabnA',
      'ZwbtK9YcgWnVkkRt',
      'Z2vUyrKQCXl1HB5M',
      'Z9AbpXHHrUvfG0-_',
      'aAEzj6iomRzHnlud',
      'aCHv9aAz1gI-kekC',
      'aI2QiBMm8i5CGHUl',
      'aJE4_xgySnm0YiEA',
      'aKe-sakkjVZfblbU',
      'aL1fJ-dSmjJh3dBv',
      'aPZxJRijW5MQEIbU',
      'aSBNy2tpMFt9_q_x',
      'aTMC_JSTHCUshvT_',
      'aVtNc4PLkRj7YjfA',
      'aYGJUTXERKzsNvqJ',
      'aZM-XMO4iwgtApgp',
      'adk4ZBCWoPJxjxki',
      'ak75qKwOa1OKtERi',
      'XcEkvOnxHgAZQ2mH',
      'XdznBmaJzwAEfikp',
      'XnFCrQ4KBgAEKnqJ',
      'XqHbWU6i5AAE0Pd5',
      'XvJKdgDSxAAEcgL2',
      'X0U6U4TiaAAE_0Up',
      'X5wtaUYnogAE9JAz',
      'X-F1VyDgDAAEIK0o',
      'YEL9yB1WNgAEgNje',
      'YJ75qTtLOQAEP3bP',
      'YSvJnHawgoVbcHx4',
      'YjLIvzy_Rxe2NDVI',
      'YlRSQMLS_yFiZKhn',
      'YlhHbI2g1V1zx5Av',
      'YuufM7QCGaJHirbD',
      'Yv0nAr8gkgaDFNw_',
      'Y8aXkLElQQXaNZqb',
      'Y_1hW_C8ptHLVPEQ',
      'ZCIEyuJ_0rCWXRHW',
      'ZEjt8rAyI2OuxPXc',
      'ZE9l5WEwTNRpNLpA',
      'ZKgdH43m3wzGXZjy',
      'ZVPvBl12kwRuWJxP',
      'ZXC916zOOkw-pJxS',
      'ZXtt2oeDtdxD94yK',
      'ZaV2GZokLNpMV4Ad',
      'ZoxkgQOfStJYENJ9',
      'Z1HpkkLXoyWFRSYr',
      'Z3lZu3f3YuoEwmzI',
      'Z9QzDkVF7R0HylYb',
      'aAACAahOJn74KdzF',
      'aC2CEld5hPqbtaJ1',
      'aHlX9LiUwZM0-QUa',
      'aKRc4uHG-YSptn3B',
      'aLnngoytmLeoNU5k',
      'aSVcdbbeLKfh8_84',
      'aTA5-edVQFD8LEee',
      'aURtRoEGXUEdFvku',
      'aZT0Jv3T8EazBYlr',
      'adQOnHAT4QQsy01R',
      'ajRWFaaY6xSY_gjG',
      'aoTnP4gG26H_hc-R',
      'ao03SS82ZisoyVx4',
      'XoRFl3aKEwAElhzg',
      'XxXyctl7_QAEkrsJ',
      'XyRQzEPIZAAEKstz',
      'X3oni6x25gAEctPp',
      'X7_AgMAMtwAEeP-Z',
      'YE-Ujrk6nQAE1rbU',
      'YFkXX5WuWwAEQbcB',
      'YF03q1gs8QAEyd6t',
      'YJReIyaeIQAEjMjO',
      'YNSdv4g1aAAETLDv',
      'Ye9BvKFimyNWH6aB',
      'YgTuuo25yFgdFDz4',
      'Ykoj1HCd_ilFDMvj',
      'Y2vNuoaDleJ_mXuc',
      'Y6fVhij-bMaGtUk2',
      'ZBlAszU5AzdcyPvp',
      'ZHe1-5T_VT4JM6Se',
      'ZINf2DyJRWrCxXQv',
      'ZK7n7InxykN_0IzX',
      'ZOxcjpv7NimoKJvP',
      'ZRWW0PV5-9rS1X84',
      'ZaCJJt25G4Mrr3dp',
      'Zr48HWoydSWLSM9H',
      'Zs7P8mC0MRETEOUz',
      'ZzxU93xn-o55VX6z',
      'Z1C1I5XmC5racGyC',
      'Z2cBAQ1H_emIN6oT',
      'Z4QGZGS_xYdn4g_S',
      'Z45qpXNTXS1f_8eu',
      'Z47ThTYngnpOYVN_',
      'Z6MptKsq6dmLdpKn',
      'Z8joFXoz7RxRs4aT',
      'aDXbVGxFpi4Y37p5',
      'aEHI7R_jL9Z4dW7B',
      'aHUkNHQ-tfBLPpL6',
      'aKbvbKkkjVZfBJlg',
      'aMgzloKfwvIy7qEU',
      'aRN6ZcRym8PKkJhP',
      'aS9MzLow2KbMMTTy',
      'ailN5Qgxrg25BtGj',
      'alBWbtcbTC9ftgy0',
      'XPjzYgifegAg2Maf',
      'Xxkal61s3wAENq3F',
      'YLV6KDwxwQAEJ3dk',
      'YMdTzmhf2QAEMouN',
      'YQNQOC1IDgAE0mTy',
      'YWVDnDp27mps9riV',
      'YZJridsfa4oyHeNd',
      'YgK_qJIBlBAXhRBK',
      'Yj0IHP6RPc8k9QJR',
      'YlbA7pl9bVPkbYCW',
      'Ys5S218Zip9ItM5c',
      'YxD_6eVfEXWWzN5P',
      'Y2Ff6tOyCmcfAyj6',
      'Y72hmHrqS4-x_zMe',
      'ZG4oUaZQTysxIBQc',
      'ZHdt4pS_nM54dHR3',
      'ZN7F7Fi713MPI9rW',
      'ZPdnGOMQdnlok8e2',
      'ZQ2I0O1DUm-08uPe',
      'ZRkKdpVH8cpCcLgl',
      'ZS9_6F2lTQdofwK4',
      'Zdi2K4C_nuL8u5bl',
      'ZeCvvhrFWujKfSP1',
      'Zfx-gLTlowVL6Geg',
      'ZkYhDYh0OTo24LgQ',
      'ZlAjMVz6PodTmHqS',
      'Zqj8stUfEbbak6Nn',
      'ZuxzNMH01AOcCpIA',
      'ZvrAJtdbJtCxO4Tg',
      'ZyhGZDho0kneKcnx',
      'Z3wpbYA_e_i3sPd-',
      'Z5JN61cdz20gqd35',
      'Z5qAmhCvUc-xaEi8',
      'Z9A6iO4tpSZ3yNNE',
      'aJFD9FqQFgVgLYTQ',
      'aO1ZiaUdM9bb6gkx',
    ]),
  },
];

const companyToBatch = new Map<string, McpRolloutBatch>();
for (const batch of mcpRolloutBatches) {
  for (const id of batch.companies) {
    companyToBatch.set(id, batch);
  }
}

export function isAlphaCustomer(companyId: string) {
  return alphaCustomers.includes(companyId) || isAlphaQuery;
}

export function isBetaCustomer(companyId: string) {
  return betaCustomers.includes(companyId);
}

export function isTimeDoctor(companyId: string) {
  return companyId === betaCustomers[0];
}

export function isTimeDoctorSales(companyId: string) {
  return companyId === timeDoctorSalesCompanyId;
}

export function getMcpRolloutBatchFlag(companyId: string): string | undefined {
  return companyToBatch.get(companyId)?.flag;
}
